import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, ChevronDown, ChevronRight, ChevronLeft, X, AlertCircle } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { companyProductSystemUseCase } from "../../../di/container";
import { companyProductSystemApiService } from "../../../infrastructure/api/company/company-product-system-api-service";
import type {
  MaterialDto,
  MaterialSheetPaperDto,
  MaterialRollPaperDto,
  RollSizeSpecDto,
  SheetSizeSpecDto,
  SheetSizePresetType,
  InkDto,
  InkConfigDto,
  OtherMaterialDto,
  PaperSpecDto,
} from "../../../domain/types/company/company-product-system";
import { MATERIAL_TYPE_LABELS } from "../../../domain/types/company/company-product-system";
import * as ExcelJS from "exceljs";
import CheckBox from "../../components/UI/CheckBox";

// ===== Label Constants =====
const SHEET_SIZE_PRESET_LABELS: Record<string, string> = {
  TYPE_A_FULL: "A0(전지)", TYPE_A_CUT2: "A1(2절)", TYPE_A_CUT4: "A2(4절)",
  TYPE_A_CUT8: "A3(8절)", TYPE_A_CUT16: "A4(16절)", TYPE_A_CUT32: "A5(32절)",
  TYPE_B_FULL: "B0(전지)", TYPE_B_CUT2: "B1(2절)", TYPE_B_CUT4: "B2(4절)",
  TYPE_B_CUT8: "B3(8절)", TYPE_B_CUT16: "B4(16절)", TYPE_B_CUT32: "B5(32절)",
};

// 롤 용지 규격 "빠른 생성" 버튼 목록. 백엔드에는 프리셋(enum) 개념이 없고 roll_size_specs는
// 전부 동등한 일반 row이므로, 이 상수는 UI 편의를 위한 것일 뿐 - 클릭 시 이 name/width 값
// 그대로 일반 규격 생성 API(createRollSizeSpec)를 호출한다. 폭은 ISO 216 종이 규격 기준.
interface RollSizeQuickPreset {
  label: string;
  width: number;
}

const ROLL_SIZE_QUICK_PRESETS: RollSizeQuickPreset[] = [
  { label: "A3", width: 297 },
  { label: "B3", width: 353 },
  { label: "A2", width: 420 },
  { label: "B2", width: 500 },
  { label: "A1", width: 594 },
  { label: "B1", width: 707 },
  { label: "A0", width: 841 },
  { label: "B0", width: 1000 },
];

const BASE_AREA_UNIT_LABELS: Record<string, string> = {
  SQUARE_M: "m²", SQUARE_CM: "cm²", SQUARE_MM: "mm²",
};

const MATERIAL_TYPES = [
  { key: "SHEET_PAPER", label: "낱장 용지" },
  { key: "ROLL_PAPER", label: "롤 용지" },
  { key: "INK_TAB", label: "잉크" },  // 잉크 탭: INK + INK_CONFIG 통합 표시
  { key: "OTHER", label: "기타" },
];

// 자재 추가 모달용 타입 (INK, INK_CONFIG 분리)
const ADD_MATERIAL_TYPES = [
  { key: "SHEET_PAPER", label: "낱장 용지" },
  { key: "ROLL_PAPER", label: "롤 용지" },
  { key: "INK_CONFIG", label: "인쇄 구성" },
  { key: "INK", label: "잉크" },
  { key: "OTHER", label: "기타" },
];

// 통일된 뱃지 색상 (파란색 기반)
const TYPE_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  SHEET_PAPER: { bg: "#E8EFFF", text: "#4285F4" },
  ROLL_PAPER: { bg: "#E8EFFF", text: "#4285F4" },
  INK_CONFIG: { bg: "#E8EFFF", text: "#4285F4" },
  OTHER: { bg: "#f5f5f5", text: "#888" },
};

// ===== Sheet Paper Preset Dimensions =====
// 절수 프리셋의 기본 사이즈. 회사가 이 절수 규격을 처음 만들 때의 "씨앗" 값이며,
// 만들어진 뒤에는 서버(sheet_size_specs)의 값이 진실이다 — 화면도 규격이 있으면 그 값을 쓴다.
//
// ※ 값 정정: 예전 이 상수들은 백엔드 SheetSizePresetType 과 달랐다(2절이 가로/세로 뒤집혀
//   있었고, 46전지가 728x1030 으로 적혀 있었다). 저장은 언제나 백엔드 enum 값으로 됐으므로
//   화면에 표시되던 숫자만 틀린 상태였다. 이제 규격 마스터가 사이즈의 단일 출처이므로
//   백엔드 enum 값에 맞춘다.
interface PaperPreset {
  type: string;
  label: string;
  width: number;
  height: number;
}

// A전지 (국전지): 636 × 939mm
const A_FULL_W = 636, A_FULL_H = 939;
const A_FULL_PRESET: PaperPreset = { type: "TYPE_A_FULL", label: "전지", width: A_FULL_W, height: A_FULL_H };
const A_PRESETS: PaperPreset[] = [
  { type: "TYPE_A_CUT2", label: "2절", width: 468, height: 636 },
  { type: "TYPE_A_CUT4", label: "4절", width: 318, height: 468 },
  { type: "TYPE_A_CUT8", label: "8절", width: 234, height: 318 },
  { type: "TYPE_A_CUT16", label: "16절", width: 159, height: 234 },
  { type: "TYPE_A_CUT32", label: "32절", width: 117, height: 159 },
];
const A_ALL_PRESETS: PaperPreset[] = [A_FULL_PRESET, ...A_PRESETS];

// B전지 (4×6전지): 788 × 1090mm
const B_FULL_W = 788, B_FULL_H = 1090;
const B_FULL_PRESET: PaperPreset = { type: "TYPE_B_FULL", label: "전지", width: B_FULL_W, height: B_FULL_H };
const B_PRESETS: PaperPreset[] = [
  { type: "TYPE_B_CUT2", label: "2절", width: 545, height: 788 },
  { type: "TYPE_B_CUT4", label: "4절", width: 394, height: 545 },
  { type: "TYPE_B_CUT8", label: "8절", width: 272, height: 394 },
  { type: "TYPE_B_CUT16", label: "16절", width: 197, height: 272 },
  { type: "TYPE_B_CUT32", label: "32절", width: 136, height: 197 },
];
const B_ALL_PRESETS: PaperPreset[] = [B_FULL_PRESET, ...B_PRESETS];

// 절수 규격을 회사 규격 풀에 처음 만들 때 붙이는 이름.
// V35 마이그레이션이 레거시 데이터를 이관할 때 쓴 이름과 반드시 같아야 한다 — 다르면 이관된
// 회사와 새 회사의 규격명이 갈린다.
const SHEET_PRESET_SPEC_NAMES: Record<string, string> = {
  TYPE_A_FULL: "국전지", TYPE_A_CUT2: "국전지 2절", TYPE_A_CUT4: "국전지 4절",
  TYPE_A_CUT8: "국전지 8절", TYPE_A_CUT16: "국전지 16절", TYPE_A_CUT32: "국전지 32절",
  TYPE_B_FULL: "46전지", TYPE_B_CUT2: "46전지 2절", TYPE_B_CUT4: "46전지 4절",
  TYPE_B_CUT8: "46전지 8절", TYPE_B_CUT16: "46전지 16절", TYPE_B_CUT32: "46전지 32절",
};

// 낱장 용지 커스텀 규격 "빠른 생성" 버튼 목록.
// 롤의 ROLL_SIZE_QUICK_PRESETS 와 같은 성격 — 백엔드에 이런 프리셋 개념은 없고, 클릭하면
// 이 이름/사이즈가 입력칸에 채워질 뿐이다. 값은 ISO 216 기준.
// (주의: 국전지/46전지는 ISO 규격이 아니다. 고객이 말하는 "A0"는 여기 841x1189 쪽이다.)
interface SheetSizeQuickPreset {
  label: string;
  width: number;
  height: number;
}

const SHEET_SIZE_QUICK_PRESETS: SheetSizeQuickPreset[] = [
  { label: "A0", width: 841, height: 1189 },
  { label: "A1", width: 594, height: 841 },
  { label: "A2", width: 420, height: 594 },
  { label: "A3", width: 297, height: 420 },
  { label: "A4", width: 210, height: 297 },
  { label: "B0", width: 1000, height: 1414 },
  { label: "B1", width: 707, height: 1000 },
  { label: "B2", width: 500, height: 707 },
];

// Scale: 용지 시각화 크기 조정 (반응형을 위해 더 작게)
const SHEET_SCALE = 400 / B_FULL_H; // ≈ 0.367

// 전지 금액 기준 절수별 자동 계산에서 절수 한 칸의 변경 내역
interface CutPricePlanRow {
  item: MaterialSheetPaperDto;
  label: string;
  currentPrice: number;
  newPrice: number;
}

// recalculated = 다시 계산할 칸, manual = 관리자가 직접 고친 것으로 보여 기본으로 남겨 둘 칸
interface CutPricePlan {
  recalculated: CutPricePlanRow[];
  manual: CutPricePlanRow[];
}

// ===== Sheet Paper Sub-resource (Visual + Table Layout) =====
const SheetPaperSection: React.FC<{ companyId: number; materialId: number }> = ({ companyId, materialId }) => {
  const [items, setItems] = useState<MaterialSheetPaperDto[]>([]);
  // 회사 규격 풀. 절수 프리셋 규격(presetType != null)과 커스텀 규격(presetType == null)이 섞여 있다.
  const [specs, setSpecs] = useState<SheetSizeSpecDto[]>([]);
  const [loading, setLoading] = useState(true);
  // 뷰 모드: "cut" = 절수 뷰 (시각적 레이아웃), "size" = 사이즈 뷰 (테이블 리스트)
  const [viewMode, setViewMode] = useState<"cut" | "size">("cut");
  const [tableTab, setTableTab] = useState<"A" | "B">("A");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingSource, setEditingSource] = useState<"visual" | "table" | "custom" | null>(null);
  const [editPrice, setEditPrice] = useState("");
  const [batchPrice, setBatchPrice] = useState("");
  const [showBatch, setShowBatch] = useState(false);
  // 전지 금액 인라인 편집 상태
  const [editingFullType, setEditingFullType] = useState<"A" | "B" | null>(null);
  const [editFullPrice, setEditFullPrice] = useState("");
  // 절수별 자동 계산 확인 모달 (resolve 로 관리자의 선택을 돌려준다)
  const [cutPrompt, setCutPrompt] = useState<{
    plan: CutPricePlan;
    fullLabel: string;
    fullPrice: number;
    cancelLabel: string;
    resolve: (result: { overwriteManual: boolean } | null) => void;
  } | null>(null);
  const [overwriteManualCuts, setOverwriteManualCuts] = useState(false);

  // 커스텀 규격 추가 모달 (롤 용지 섹션과 동일한 구성)
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newSpecName, setNewSpecName] = useState("");
  const [newSpecWidth, setNewSpecWidth] = useState("");
  const [newSpecHeight, setNewSpecHeight] = useState("");
  const [editingSpecId, setEditingSpecId] = useState<number | null>(null);
  const [editSpecName, setEditSpecName] = useState("");
  const [editSpecWidth, setEditSpecWidth] = useState("");
  const [editSpecHeight, setEditSpecHeight] = useState("");

  const load = useCallback(async () => {
    try {
      const [sheetPapersRes, specsRes] = await Promise.all([
        companyProductSystemApiService.getSheetPapers(companyId, materialId),
        companyProductSystemApiService.getSheetSizeSpecs(companyId),
      ]);
      setItems(sheetPapersRes.data || []);
      setSpecs(specsRes.data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, materialId]);

  useEffect(() => { load(); }, [load]);

  const itemByPreset = new Map(items.filter(i => i.presetType).map(i => [i.presetType as string, i]));
  // 이 자재에 연결된 커스텀 규격(절수가 아닌 것)들
  const customItems = items.filter(i => !i.presetType).sort((a, b) => a.width * a.height - b.width * b.height);
  // 회사 규격 풀 중 커스텀이면서 아직 이 자재에 안 붙은 것 (모달 "기존 규격 불러오기"용)
  const linkedSpecIds = new Set(items.map(i => i.sheetSizeSpecId));
  const unlinkedCustomSpecs = specs
    .filter(s => !s.presetType && !linkedSpecIds.has(s.id))
    .sort((a, b) => a.width * a.height - b.width * b.height);

  /**
   * 절수 프리셋 칸을 처음 누르면 그 절수의 회사 규격이 아직 없을 수 있다(V35 이후 생성된 회사).
   * 있으면 그대로 쓰고, 없으면 프리셋 기본 사이즈로 만들어서 쓴다.
   */
  const ensureSpecForPreset = async (preset: PaperPreset): Promise<SheetSizeSpecDto | null> => {
    const existing = specs.find(s => s.presetType === preset.type);
    if (existing) return existing;
    const res = await companyProductSystemApiService.createSheetSizeSpec(companyId, {
      name: SHEET_PRESET_SPEC_NAMES[preset.type] || preset.label,
      width: preset.width,
      height: preset.height,
      presetType: preset.type as SheetSizePresetType,
    });
    return res.data ?? null;
  };

  const handleAdd = async (preset: PaperPreset) => {
    try {
      const spec = await ensureSpecForPreset(preset);
      if (!spec) { alert("규격을 만들지 못했습니다."); return; }
      await companyProductSystemApiService.createSheetPaper(companyId, materialId, {
        sheetSizeSpecId: spec.id,
        priceTiers: [{ minQuantity: 1, price: 0 }],
      } as any);
      await load();
    } catch (e: any) { alert(e?.message || "추가에 실패했습니다."); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await companyProductSystemApiService.deleteSheetPaper(companyId, materialId, id);
      await load();
    } catch { alert("삭제에 실패했습니다."); }
  };

  // ===== 커스텀 규격 (롤 용지 섹션의 규격 풀 CRUD 와 동일한 흐름) =====

  const handleAddSpecToMaterial = async (spec: SheetSizeSpecDto) => {
    await companyProductSystemApiService.createSheetPaper(companyId, materialId, {
      sheetSizeSpecId: spec.id,
      priceTiers: [{ minQuantity: 1, price: 0 }],
    } as any);
    await load();
  };

  const handleCreateSpec = async () => {
    const width = Number(newSpecWidth);
    const height = Number(newSpecHeight);
    if (!width || width <= 0 || !height || height <= 0) { alert("가로/세로(mm)를 입력해주세요."); return; }
    const name = newSpecName.trim();
    try {
      // 같은 이름의 풀 규격이 이미 있으면 재사용(중복 생성 방지) 후 연결
      const existing = name ? specs.find(s => s.name === name) : undefined;
      if (existing) {
        if (linkedSpecIds.has(existing.id)) { alert("이미 이 자재에 추가된 규격입니다."); return; }
        await handleAddSpecToMaterial(existing);
      } else {
        const res = await companyProductSystemApiService.createSheetSizeSpec(companyId, {
          name: name || undefined,
          width,
          height,
        });
        // 생성 직후 이 자재에 바로 연결 (2-call)
        if (res.data) await handleAddSpecToMaterial(res.data);
        else await load();
      }
      setNewSpecName("");
      setNewSpecWidth("");
      setNewSpecHeight("");
      setShowAddMenu(false);
    } catch (e: any) { alert(e?.message || "규격 생성에 실패했습니다."); }
  };

  // 프리셋/풀 규격 선택 → 바로 추가하지 않고 아래 입력칸을 채워 확인 후 "추가"로 확정
  const handleFillFromQuickPreset = (preset: SheetSizeQuickPreset) => {
    setNewSpecName(preset.label);
    setNewSpecWidth(String(preset.width));
    setNewSpecHeight(String(preset.height));
  };
  const handleFillFromSpec = (spec: SheetSizeSpecDto) => {
    setNewSpecName(spec.name);
    setNewSpecWidth(String(spec.width));
    setNewSpecHeight(String(spec.height));
  };

  const handleUpdateSpec = async (spec: SheetSizeSpecDto) => {
    const width = Number(editSpecWidth);
    const height = Number(editSpecHeight);
    if (!width || width <= 0 || !height || height <= 0) { alert("가로/세로(mm)를 입력해주세요."); return; }
    try {
      await companyProductSystemApiService.updateSheetSizeSpec(companyId, spec.id, {
        name: editSpecName.trim() || undefined,
        width,
        height,
      });
      setEditingSpecId(null);
      await load();
    } catch (e: any) { alert(e?.message || "규격 수정에 실패했습니다."); }
  };

  const handleDeleteSpec = async (spec: SheetSizeSpecDto) => {
    if (!window.confirm(`"${spec.name}" 규격을 목록에서 삭제하시겠습니까?`)) return;
    try {
      await companyProductSystemApiService.deleteSheetSizeSpec(companyId, spec.id);
      await load();
    } catch (e: any) { alert(e?.message || "이 규격을 사용 중인 자재가 있어 삭제할 수 없습니다."); }
  };

  // 절수별 분할 비율: 전지=1, 2절=1/2, 4절=1/4, 8절=1/8, 16절=1/16, 32절=1/32
  const CUT_DIVISORS: Record<string, number> = {
    TYPE_A_FULL: 1, TYPE_A_CUT2: 2, TYPE_A_CUT4: 4, TYPE_A_CUT8: 8, TYPE_A_CUT16: 16, TYPE_A_CUT32: 32,
    TYPE_B_FULL: 1, TYPE_B_CUT2: 2, TYPE_B_CUT4: 4, TYPE_B_CUT8: 8, TYPE_B_CUT16: 16, TYPE_B_CUT32: 32,
  };

  // 절수 한 칸의 자동 계산 금액 (전지 금액 ÷ 절수)
  const calcCutPrice = (fullPrice: number, presetType: string) =>
    Math.round(fullPrice / (CUT_DIVISORS[presetType] || 1));

  // 첫 구간(기본 금액)만 바꾸고 나머지 수량 구간은 그대로 둔다
  const withBasePrice = (sp: MaterialSheetPaperDto, price: number) =>
    sp.priceTiers.length > 0
      ? sp.priceTiers.map((t, i) => i === 0 ? { minQuantity: t.minQuantity, price } : { minQuantity: t.minQuantity, price: t.price })
      : [{ minQuantity: 1, price }];

  const saveSheetPrice = (sp: MaterialSheetPaperDto, price: number) =>
    companyProductSystemApiService.updateSheetPaper(companyId, materialId, sp.id, {
      sheetSizeSpecId: sp.sheetSizeSpecId, paperColor: sp.paperColor, priceTiers: withBasePrice(sp, price),
    } as any);

  /**
   * 전지 금액이 바뀔 때 절수별 금액을 무엇을 다시 계산하고 무엇을 남길지 나눈다.
   *
   * 지금 금액이 "바뀌기 전 전지 금액"으로 자동 계산한 값과 다르면 관리자가 직접 고친 금액으로 본다.
   * 0원(미설정)인 칸과 새 금액과 이미 같은 칸은 직접 입력으로 치지 않는다.
   */
  const planCutPrices = (type: "A" | "B", oldFullPrice: number, newFullPrice: number): CutPricePlan => {
    const prefix = type === "A" ? "TYPE_A_" : "TYPE_B_";
    const rows: CutPricePlanRow[] = items
      .filter((i) => !!i.presetType && i.presetType.startsWith(prefix) && (CUT_DIVISORS[i.presetType] || 1) > 1)
      .sort((a, b) => CUT_DIVISORS[a.presetType!] - CUT_DIVISORS[b.presetType!])
      .map((item) => ({
        item,
        label: SHEET_SIZE_PRESET_LABELS[item.presetType!] || item.presetType!,
        currentPrice: getPrice(item),
        newPrice: calcCutPrice(newFullPrice, item.presetType!),
      }));
    const isManual = (r: CutPricePlanRow) =>
      r.currentPrice > 0 &&
      r.currentPrice !== calcCutPrice(oldFullPrice, r.item.presetType!) &&
      r.currentPrice !== r.newPrice;
    return {
      recalculated: rows.filter((r) => !isManual(r)),
      manual: rows.filter(isManual),
    };
  };

  // 확인 모달을 띄우고 관리자의 선택을 기다린다 (null = 절수 금액은 건드리지 않음)
  const askCutPriceApply = (plan: CutPricePlan, fullLabel: string, fullPrice: number, cancelLabel: string) =>
    new Promise<{ overwriteManual: boolean } | null>((resolve) => {
      setOverwriteManualCuts(false);
      setCutPrompt({ plan, fullLabel, fullPrice, cancelLabel, resolve });
    });

  const closeCutPrompt = (result: { overwriteManual: boolean } | null) => {
    cutPrompt?.resolve(result);
    setCutPrompt(null);
  };

  const applyCutPlan = async (plan: CutPricePlan, overwriteManual: boolean) => {
    const targets = overwriteManual ? [...plan.recalculated, ...plan.manual] : plan.recalculated;
    for (const row of targets) {
      if (row.currentPrice === row.newPrice) continue;
      await saveSheetPrice(row.item, row.newPrice);
    }
  };

  // 전지 금액을 저장한 뒤, 절수별 금액을 다시 계산할지 묻고 적용한다
  const proposeCutRecalc = async (type: "A" | "B", oldFullPrice: number, newFullPrice: number) => {
    if (newFullPrice <= 0) return;
    const plan = planCutPrices(type, oldFullPrice, newFullPrice);
    if (plan.recalculated.length + plan.manual.length === 0) return;
    const choice = await askCutPriceApply(
      plan,
      type === "A" ? "국전지 (A전지)" : "4×6전지 (B전지)",
      newFullPrice,
      "절수 금액은 그대로 두기",
    );
    if (choice) await applyCutPlan(plan, choice.overwriteManual);
  };

  const handlePriceUpdate = async (sp: MaterialSheetPaperDto, newPrice: number) => {
    // 저장하기 전 금액이 있어야 직접 고친 절수 금액을 가려낼 수 있다
    const oldPrice = getPrice(sp);
    try {
      await saveSheetPrice(sp, newPrice);

      // 전지 가격 수정 시 절수별 자동 계산 제안 (같은 전지 계열의 절수만)
      if (sp.presetType === "TYPE_A_FULL" || sp.presetType === "TYPE_B_FULL") {
        await proposeCutRecalc(sp.presetType === "TYPE_A_FULL" ? "A" : "B", oldPrice, newPrice);
      }

      await load();
    } catch { alert("가격 수정에 실패했습니다."); }
  };

  const handleBatchApply = async () => {
    const fullPrice = Number(batchPrice);
    if (isNaN(fullPrice) || fullPrice <= 0) return;

    // 현재 선택된 탭에 해당하는 전지만 골라 직접 적용하고, 절수는 확인을 거친다
    const prefixToMatch = tableTab === "A" ? "TYPE_A_" : "TYPE_B_";
    const fullItems = items.filter(sp => sp.presetType?.startsWith(prefixToMatch) && (CUT_DIVISORS[sp.presetType] || 1) === 1);
    const plan = planCutPrices(tableTab, getFullSheetPrice(tableTab) ?? 0, fullPrice);

    let overwriteManual = false;
    if (plan.recalculated.length + plan.manual.length > 0) {
      const choice = await askCutPriceApply(
        plan,
        tableTab === "A" ? "국전지 (A전지)" : "4×6전지 (B전지)",
        fullPrice,
        "취소",
      );
      if (!choice) return;
      overwriteManual = choice.overwriteManual;
    }

    try {
      for (const sp of fullItems) {
        await saveSheetPrice(sp, fullPrice);
      }
      await applyCutPlan(plan, overwriteManual);
      await load();
      setShowBatch(false);
      setBatchPrice("");
    } catch { alert("일괄 적용에 실패했습니다."); }
  };

  const getPrice = (sp: MaterialSheetPaperDto): number =>
    sp.priceTiers.length > 0 ? sp.priceTiers[0].price : 0;

  // 전지 금액 가져오기
  const getFullSheetPrice = (type: "A" | "B"): number | null => {
    const presetType = type === "A" ? "TYPE_A_FULL" : "TYPE_B_FULL";
    const fullSheet = items.find(i => i.presetType === presetType);
    return fullSheet ? getPrice(fullSheet) : null;
  };

  // 전지 금액 업데이트 (절수별 자동 계산 포함)
  const handleFullSheetPriceUpdate = async (type: "A" | "B", newPrice: number) => {
    const presetType = type === "A" ? "TYPE_A_FULL" : "TYPE_B_FULL";
    const fullSheet = items.find(i => i.presetType === presetType);
    const fullPreset = type === "A" ? A_FULL_PRESET : B_FULL_PRESET;
    const oldPrice = fullSheet ? getPrice(fullSheet) : 0;

    try {
      // 전지가 없으면 먼저 생성
      if (!fullSheet) {
        const spec = await ensureSpecForPreset(fullPreset);
        if (!spec) { alert("규격을 만들지 못했습니다."); return; }
        await companyProductSystemApiService.createSheetPaper(companyId, materialId, {
          sheetSizeSpecId: spec.id,
          priceTiers: [{ minQuantity: 1, price: newPrice }],
        } as any);
      } else {
        // 전지 가격 업데이트
        await saveSheetPrice(fullSheet, newPrice);
      }

      // 절수별 자동 계산 적용
      await proposeCutRecalc(type, oldPrice, newPrice);

      await load();
    } catch { alert("전지 가격 수정에 실패했습니다."); }
  };

  // 절수를 규격명으로 변환 (2절 → A1/B1, 4절 → A2/B2 등 - 실제 종이 규격 기준)
  // 2절 = 전지의 1/2 ≈ A1, 4절 = 전지의 1/4 ≈ A2, 8절 ≈ A3, 16절 ≈ A4, 32절 ≈ A5
  const cutToSizeLabel: Record<string, string> = {
    TYPE_A_FULL: "A0", TYPE_A_CUT2: "A1", TYPE_A_CUT4: "A2", TYPE_A_CUT8: "A3", TYPE_A_CUT16: "A4", TYPE_A_CUT32: "A5",
    TYPE_B_FULL: "B0", TYPE_B_CUT2: "B1", TYPE_B_CUT4: "B2", TYPE_B_CUT8: "B3", TYPE_B_CUT16: "B4", TYPE_B_CUT32: "B5",
  };

  // 이 절수의 실제 사이즈. 회사 규격이 이미 있으면 그 값이 진실이고(회사가 수정했을 수 있다),
  // 아직 없으면 프리셋 기본값을 보여준다.
  const presetDims = (preset: PaperPreset): { width: number; height: number } => {
    const spec = specs.find(s => s.presetType === preset.type);
    return spec ? { width: spec.width, height: spec.height } : { width: preset.width, height: preset.height };
  };

  // 뷰 모드에 따른 라벨 표시: 절수 = "2절(468x636)", 사이즈 = "A1(468x636)"
  const getDisplayLabel = (preset: PaperPreset) => {
    const { width, height } = presetDims(preset);
    if (viewMode === "cut") {
      return `${preset.label}(${width}x${height})`;
    } else {
      const sizeLabel = cutToSizeLabel[preset.type] || preset.label;
      return `${sizeLabel}(${width}x${height})`;
    }
  };

  // Render a cell inside the visual layout
  const renderCell = (preset: PaperPreset) => {
    const existing = itemByPreset.get(preset.type);

    if (existing) {
      const price = getPrice(existing);
      const isEditing = editingId === existing.id && editingSource === "visual";
      return (
        <div className="flex flex-col items-center justify-center h-full w-full relative group">
          <span className="text-[11px] text-[#555] font-medium leading-tight">
            {getDisplayLabel(preset)}
          </span>
          {isEditing ? (
            <div className="flex items-center gap-0.5 mt-1">
              <input type="number" autoFocus
                className="w-[60px] border border-[#4285F4] rounded-[3px] px-1 py-0.5 text-[11px] text-center focus:outline-none"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                onBlur={() => { const p = Number(editPrice); if (!isNaN(p)) handlePriceUpdate(existing, p); setEditingId(null); setEditingSource(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setEditingId(null); setEditingSource(null); } }}
              />
              <span className="text-[10px] text-[#999]">원</span>
            </div>
          ) : (
            <button onClick={() => { setEditingId(existing.id); setEditingSource("visual"); setEditPrice(String(price)); }}
              className="mt-1 px-2.5 py-0.5 bg-[#eee] rounded-[3px] text-[11px] font-bold text-[#333] hover:bg-[#ddd]">
              {price.toLocaleString()}원
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); handleDelete(existing.id); }}
            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 text-[#ccc] hover:text-[#F24A4A] transition-opacity">
            <Trash2 className="w-[11px] h-[11px]" />
          </button>
        </div>
      );
    }

    return (
      <button onClick={() => handleAdd(preset)}
        className="flex flex-col items-center justify-center h-full w-full hover:bg-[#f0f4ff] transition-colors group/add">
        <Plus className="w-[16px] h-[16px] text-[#ccc] group-hover/add:text-[#4285F4]" />
        <span className="text-[10px] text-[#bbb] group-hover/add:text-[#4285F4] leading-tight">
          {getDisplayLabel(preset)}
        </span>
        <span className="text-[9px] text-[#ccc] group-hover/add:text-[#4285F4]">추가하기</span>
      </button>
    );
  };

  // Render the proportional visual layout for a paper series
  const renderVisualLayout = (presets: PaperPreset[], fullW: number, fullH: number) => {
    const w = Math.round(fullW * SHEET_SCALE);
    const h = Math.round(fullH * SHEET_SCALE);
    const c2 = presets[0], c4 = presets[1], c8 = presets[2], c16 = presets[3], c32 = presets[4];

    // 정확한 크기 계산
    const halfW = Math.round(w / 2);
    const halfH = Math.round(h / 2);
    const quarterW = Math.round(w / 4);
    const quarterH = Math.round(h / 4);

    return (
      <div className="border border-[#ccc] rounded-[4px] overflow-hidden bg-white flex-shrink-0"
        style={{ width: w, height: h }}>
        {/* 2절 - top half */}
        <div className="border-b border-[#ccc]" style={{ height: halfH }}>
          {renderCell(c2)}
        </div>
        {/* bottom half */}
        <div className="flex" style={{ height: halfH }}>
          {/* Left: 8절 + (32절 + 16절) */}
          <div className="flex flex-col border-r border-[#ccc]" style={{ width: halfW }}>
            <div className="border-b border-[#ccc]" style={{ height: quarterH }}>
              {renderCell(c8)}
            </div>
            <div className="flex" style={{ height: quarterH }}>
              <div className="border-r border-[#ccc]" style={{ width: quarterW }}>
                {renderCell(c32)}
              </div>
              <div style={{ width: quarterW }}>
                {renderCell(c16)}
              </div>
            </div>
          </div>
          {/* Right: 4절 */}
          <div style={{ width: halfW, height: halfH }}>
            {renderCell(c4)}
          </div>
        </div>
      </div>
    );
  };

  // Render a table row for a preset
  const renderTableRow = (preset: PaperPreset) => {
    const existing = itemByPreset.get(preset.type);
    const dims = presetDims(preset);
    // 뷰 모드에 따른 테이블 표시: 절수 = "2절", 사이즈 = "A2"
    const firstColumnLabel = viewMode === "cut"
      ? preset.label
      : (cutToSizeLabel[preset.type] || preset.label);

    if (existing) {
      const price = getPrice(existing);
      const isEditing = editingId === existing.id && editingSource === "table";
      return (
        <tr key={preset.type} className="border-b border-[#eee]">
          <td className="px-3 py-2 text-[12px] font-medium text-[#333] text-center">{firstColumnLabel}</td>
          <td className="px-3 py-2 text-[12px] text-[#666] text-center">{dims.width} × {dims.height}</td>
          <td className="px-3 py-2 text-center">
            {isEditing ? (
              <input type="number" autoFocus
                className="w-[80px] border border-[#4285F4] rounded-[3px] px-1.5 py-0.5 text-[12px] text-center focus:outline-none"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                onBlur={() => { const p = Number(editPrice); if (!isNaN(p)) handlePriceUpdate(existing, p); setEditingId(null); setEditingSource(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setEditingId(null); setEditingSource(null); } }}
              />
            ) : (
              <button onClick={() => { setEditingId(existing.id); setEditingSource("table"); setEditPrice(String(price)); }}
                className="px-2 py-0.5 bg-[#f0f0f0] rounded-[3px] text-[12px] font-medium text-[#333] hover:bg-[#e5e5e5]">
                {price.toLocaleString()}원
              </button>
            )}
          </td>
          <td className="px-3 py-2 text-center">
            <button onClick={() => handleDelete(existing.id)} className="text-[#ccc] hover:text-[#F24A4A]">
              <Trash2 className="w-[13px] h-[13px]" />
            </button>
          </td>
        </tr>
      );
    }
    return (
      <tr key={preset.type} className="border-b border-[#eee]">
        <td className="px-3 py-2 text-[12px] text-[#bbb] text-center">{firstColumnLabel}</td>
        <td className="px-3 py-2 text-[12px] text-[#bbb] text-center">{dims.width} × {dims.height}</td>
        <td className="px-3 py-2 text-[12px] text-[#ccc] text-center">-</td>
        <td className="px-3 py-2 text-center">
          <button onClick={() => handleAdd(preset)}
            className="text-[#4285F4] hover:text-[#3367D6]">
            <Plus className="w-[14px] h-[14px]" />
          </button>
        </td>
      </tr>
    );
  };

  if (loading) return <p className="text-[13px] text-[#999] py-2">불러오는 중...</p>;

  const tablePresets = tableTab === "A" ? A_ALL_PRESETS : B_ALL_PRESETS;

  return (
    <div>
      {/* 뷰 모드 토글 - 절수 | 사이즈 */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex border border-[#222] rounded-[4px] overflow-hidden">
          <button
            onClick={() => setViewMode("cut")}
            className={`px-5 py-2 text-[13px] font-medium transition-colors ${
              viewMode === "cut"
                ? "bg-[#222] text-white"
                : "bg-white text-[#222] hover:bg-[#f5f5f5]"
            }`}
          >
            절수
          </button>
          <button
            onClick={() => setViewMode("size")}
            className={`px-5 py-2 text-[13px] font-medium border-l border-[#222] transition-colors ${
              viewMode === "size"
                ? "bg-[#222] text-white"
                : "bg-white text-[#222] hover:bg-[#f5f5f5]"
            }`}
          >
            사이즈
          </button>
        </div>
      </div>

      {/* Main layout: Visual + Table 가로 배치 (반응형) */}
      <div className="flex flex-col xl:flex-row gap-6 items-start">
        {/* Visual layouts - A전지 & B전지 */}
        <div className="w-full xl:flex-1 flex flex-wrap gap-4 items-end justify-center py-4">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-[12px] font-medium text-[#555]">국전지 (A전지)</span>
              {editingFullType === "A" ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    autoFocus
                    className="w-[80px] border border-[#4285F4] rounded-[4px] px-2 py-1 text-[12px] text-center focus:outline-none"
                    value={editFullPrice}
                    onChange={(e) => setEditFullPrice(e.target.value)}
                    onBlur={() => {
                      const p = Number(editFullPrice);
                      if (!isNaN(p) && p > 0) handleFullSheetPriceUpdate("A", p);
                      setEditingFullType(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      if (e.key === "Escape") setEditingFullType(null);
                    }}
                  />
                  <span className="text-[11px] text-[#999]">원</span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setEditingFullType("A");
                    setEditFullPrice(String(getFullSheetPrice("A") || ""));
                  }}
                  className="px-2 py-1 bg-[#E8EFFF] text-[#4285F4] rounded-[4px] text-[11px] font-medium hover:bg-[#D0E1FF] transition-colors"
                >
                  {getFullSheetPrice("A") !== null ? `${getFullSheetPrice("A")!.toLocaleString()}원` : "전지 금액 설정"}
                </button>
              )}
            </div>
            {renderVisualLayout(A_PRESETS, A_FULL_W, A_FULL_H)}
          </div>
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-[12px] font-medium text-[#555]">4×6전지 (B전지)</span>
              {editingFullType === "B" ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    autoFocus
                    className="w-[80px] border border-[#4285F4] rounded-[4px] px-2 py-1 text-[12px] text-center focus:outline-none"
                    value={editFullPrice}
                    onChange={(e) => setEditFullPrice(e.target.value)}
                    onBlur={() => {
                      const p = Number(editFullPrice);
                      if (!isNaN(p) && p > 0) handleFullSheetPriceUpdate("B", p);
                      setEditingFullType(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                      if (e.key === "Escape") setEditingFullType(null);
                    }}
                  />
                  <span className="text-[11px] text-[#999]">원</span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setEditingFullType("B");
                    setEditFullPrice(String(getFullSheetPrice("B") || ""));
                  }}
                  className="px-2 py-1 bg-[#E8EFFF] text-[#4285F4] rounded-[4px] text-[11px] font-medium hover:bg-[#D0E1FF] transition-colors"
                >
                  {getFullSheetPrice("B") !== null ? `${getFullSheetPrice("B")!.toLocaleString()}원` : "전지 금액 설정"}
                </button>
              )}
            </div>
            {renderVisualLayout(B_PRESETS, B_FULL_W, B_FULL_H)}
          </div>
        </div>

        {/* Table view */}
        <div className="w-full xl:w-[360px] xl:flex-shrink-0 py-4">
          {/* Table tabs */}
          <div className="flex mb-0">
            <button onClick={() => { setTableTab("A"); setShowBatch(false); setBatchPrice(""); }}
              className={`flex-1 px-4 py-2.5 text-[13px] font-medium border ${
                tableTab === "A"
                  ? "bg-white text-[#222] border-[#ccc] border-b-white"
                  : "bg-[#f5f5f5] text-[#999] border-[#ccc]"
              } rounded-tl-[8px]`}>
              국전지 (A전지)
            </button>
            <button onClick={() => { setTableTab("B"); setShowBatch(false); setBatchPrice(""); }}
              className={`flex-1 px-4 py-2.5 text-[13px] font-medium border border-l-0 ${
                tableTab === "B"
                  ? "bg-white text-[#222] border-[#ccc] border-b-white"
                  : "bg-[#f5f5f5] text-[#999] border-[#ccc]"
              } rounded-tr-[8px]`}>
              4×6전지 (B전지)
            </button>
          </div>

          {/* Table */}
          <div className="border border-[#ccc] border-t-0 rounded-b-[8px] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#fafafa] border-b border-[#eee]">
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">{viewMode === "cut" ? "절수" : "규격"}</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">사이즈(mm)</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">가격</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {tablePresets.map((p) => renderTableRow(p))}
              </tbody>
            </table>
          </div>

          {/* Batch price - 전지 금액 기준 자동 계산 */}
          <div className="mt-3">
            {!showBatch ? (
              <button onClick={() => setShowBatch(true)}
                className="w-full py-2 text-[12px] font-medium text-[#222] bg-white border border-[#222] rounded-[6px] hover:bg-[#f5f5f5]">
                {tableTab === "A" ? "국전지 (A전지)" : "4×6전지 (B전지)"} 금액 일괄 적용
              </button>
            ) : (
              <div className="p-3 bg-[#f9f9f9] rounded-[8px] border border-[#e0e0e0]">
                <p className="text-[11px] text-[#888] mb-2">
                  {tableTab === "A" ? "A전지" : "B전지"} 금액을 입력하면 절수별로 자동 계산됩니다. 직접 입력한 절수 금액은 적용 전에 유지할지 고를 수 있습니다.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-[#555] font-medium flex-shrink-0">
                    {tableTab === "A" ? "A전지" : "B전지"} 금액
                  </span>
                  <input type="number" value={batchPrice} onChange={(e) => setBatchPrice(e.target.value)}
                    className="flex-1 border border-[#e0e0e0] rounded-[6px] px-2 py-1.5 text-[12px] focus:border-[#4285F4] focus:outline-none"
                    placeholder="예: 10,000" autoFocus />
                  <span className="text-[12px] text-[#999]">원</span>
                </div>
                {batchPrice && Number(batchPrice) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#666]">
                    <span className="font-medium text-[#333]">전지: {Number(batchPrice).toLocaleString()}원</span>
                    <span>2절: {Math.round(Number(batchPrice) / 2).toLocaleString()}원</span>
                    <span>4절: {Math.round(Number(batchPrice) / 4).toLocaleString()}원</span>
                    <span>8절: {Math.round(Number(batchPrice) / 8).toLocaleString()}원</span>
                    <span>16절: {Math.round(Number(batchPrice) / 16).toLocaleString()}원</span>
                    <span>32절: {Math.round(Number(batchPrice) / 32).toLocaleString()}원</span>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <button onClick={handleBatchApply}
                    className="flex-1 py-1.5 text-[12px] text-white bg-[#222] rounded-[6px]">
                    {tableTab === "A" ? "A전지" : "B전지"}에 적용
                  </button>
                  <button onClick={() => { setShowBatch(false); setBatchPrice(""); }}
                    className="px-3 py-1.5 text-[12px] text-[#666]">취소</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 0원 규격 경고.
          주문 가격은 "디자인이 들어가는 용지 중 단가가 가장 싼 것"으로 계산되므로(SheetPaperSelector),
          0원으로 남겨둔 규격이 하나라도 있으면 그 자재의 주문 금액이 전부 0원으로 떨어진다. */}
      {items.some((sp) => getPrice(sp) <= 0) && (
        <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[#EBF3FF] border border-[#4285F4]/20 text-[12px] text-[#4285F4]">
          <AlertCircle className="w-[15px] h-[15px] flex-shrink-0" />
          <span>
            가격이 0원인 규격 <b>{items.filter((sp) => getPrice(sp) <= 0).length}개</b>가 있습니다.
            주문 금액은 조건에 맞는 용지 중 가장 싼 것으로 계산되므로, 0원 규격을 그대로 두면 이 자재의 주문 금액이 0원이 됩니다.
          </span>
        </div>
      )}

      {/* ===== 커스텀 규격 ===== */}
      {/* 전지/절지 체계로 표현되지 않는 규격(예: A0 841x1189)을 회사가 직접 추가한다.
          절수 그리드와 달리 칸이 고정돼 있지 않고, 규격 자체를 만들고 지울 수 있다. */}
      <div className="mt-6 pt-5 border-t border-[#eee]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-bold text-[#222]">커스텀 규격</span>
          <button onClick={() => setShowAddMenu(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 border border-[#e0e0e0] rounded-[8px] text-[12px] text-[#4285F4] hover:border-[#4285F4] hover:bg-[#f8faff]">
            <Plus className="w-[14px] h-[14px]" /> 규격 추가
          </button>
        </div>

        {customItems.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-[#999]">
            전지/절지 외의 규격(예: A0 841×1189)이 필요하면 <span className="text-[#666]">규격 추가</span>로 직접 등록할 수 있습니다.
          </p>
        ) : (
          <div className="border border-[#ccc] rounded-[4px] overflow-hidden bg-white">
            <table className="w-full">
              <thead>
                <tr className="bg-[#fafafa] border-b border-[#eee]">
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-left">규격</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">사이즈(mm)</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">가격</th>
                  <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {customItems.map((sp) => {
                  const price = getPrice(sp);
                  const isEditing = editingId === sp.id && editingSource === "custom";
                  return (
                    <tr key={sp.id} className="border-b border-[#eee]">
                      <td className="px-3 py-2 text-[12px] font-medium text-[#333]">{sp.sheetSizeSpecName}</td>
                      <td className="px-3 py-2 text-[12px] text-[#666] text-center">{sp.width} × {sp.height}</td>
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <input type="number" autoFocus
                            className="w-[80px] border border-[#4285F4] rounded-[3px] px-1.5 py-0.5 text-[12px] text-center focus:outline-none"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            onBlur={() => { const p = Number(editPrice); if (!isNaN(p)) handlePriceUpdate(sp, p); setEditingId(null); setEditingSource(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setEditingId(null); setEditingSource(null); } }}
                          />
                        ) : (
                          <button onClick={() => { setEditingId(sp.id); setEditingSource("custom"); setEditPrice(String(price)); }}
                            className="px-2 py-0.5 bg-[#f0f0f0] rounded-[3px] text-[12px] font-medium text-[#333] hover:bg-[#e5e5e5]">
                            {price.toLocaleString()}원
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => handleDelete(sp.id)} className="text-[#ccc] hover:text-[#F24A4A]">
                          <Trash2 className="w-[13px] h-[13px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] text-[#999]">
          커스텀 규격은 <span className="text-[#666]">전지 금액 일괄 적용</span>의 절수 계산 대상이 아닙니다. 가격을 직접 입력해주세요.
        </p>
      </div>

      {showAddMenu && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowAddMenu(false); setEditingSpecId(null); }}>
          <div className="bg-white rounded-[12px] w-[460px] max-w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold text-[#222]">낱장 용지 규격 추가</h3>
              <button onClick={() => { setShowAddMenu(false); setEditingSpecId(null); }}
                className="text-[#999] hover:text-[#555]">
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>

            {/* 풀에서 추가 */}
            {unlinkedCustomSpecs.length > 0 && (
              <div className="mb-5">
                <p className="text-[12px] font-medium text-[#888] mb-2">기존 규격 불러오기</p>
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                  {unlinkedCustomSpecs.map((s) => (
                    <div key={s.id} className="flex items-center gap-1.5">
                      {editingSpecId === s.id ? (
                        <>
                          <input type="text" autoFocus placeholder="이름"
                            className="w-[86px] border border-[#ccc] rounded-[6px] px-2 py-1.5 text-[12px]"
                            value={editSpecName} onChange={(e) => setEditSpecName(e.target.value)} />
                          <div className="relative w-[76px]">
                            <input type="text" inputMode="numeric"
                              className="w-full border border-[#ccc] rounded-[6px] pl-2 pr-7 py-1.5 text-[12px] text-right"
                              value={editSpecWidth} onChange={(e) => setEditSpecWidth(e.target.value.replace(/[^0-9]/g, ""))} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                          </div>
                          <span className="text-[11px] text-[#999]">×</span>
                          <div className="relative w-[76px]">
                            <input type="text" inputMode="numeric"
                              className="w-full border border-[#ccc] rounded-[6px] pl-2 pr-7 py-1.5 text-[12px] text-right"
                              value={editSpecHeight} onChange={(e) => setEditSpecHeight(e.target.value.replace(/[^0-9]/g, ""))} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                          </div>
                          <button onClick={() => handleUpdateSpec(s)} className="text-[#4285F4] text-[12px] px-1">저장</button>
                          <button onClick={() => setEditingSpecId(null)} className="text-[#999] text-[12px] px-1">취소</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleFillFromSpec(s)}
                            className="flex-1 flex items-center justify-between px-2.5 py-2 rounded-[8px] text-[13px] text-[#333] hover:bg-[#f5f5f5] border border-[#f0f0f0]">
                            <span>{s.name}<span className="text-[#999] ml-1">({s.width}×{s.height}mm)</span></span>
                            <span className="text-[12px] text-[#4285F4]">불러오기</span>
                          </button>
                          <button onClick={() => { setEditingSpecId(s.id); setEditSpecName(s.name); setEditSpecWidth(String(s.width)); setEditSpecHeight(String(s.height)); }}
                            className="text-[#bbb] hover:text-[#666] text-[11px] px-1">수정</button>
                          <button onClick={() => handleDeleteSpec(s)} className="text-[#ccc] hover:text-[#F24A4A]">
                            <Trash2 className="w-[13px] h-[13px]" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 새 규격 만들기 */}
            <div>
              <p className="text-[12px] font-medium text-[#888] mb-2">새 규격 만들기</p>
              <p className="text-[11px] text-[#aaa] mb-2">아래 프리셋을 누르면 입력칸에 채워집니다. 확인 후 <span className="text-[#666]">추가</span>를 눌러주세요.</p>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {SHEET_SIZE_QUICK_PRESETS.map((p) => (
                  <button key={p.label} type="button" onClick={() => handleFillFromQuickPreset(p)}
                    className="px-2.5 py-1.5 border border-[#e0e0e0] rounded-[6px] text-[12px] text-[#666] hover:border-[#4285F4] hover:text-[#4285F4]">
                    {p.label}({p.width}×{p.height})
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 items-center">
                <input type="text" placeholder="이름 (선택, 비우면 자동생성)"
                  className="flex-1 min-w-0 border border-[#e0e0e0] rounded-[8px] px-2.5 py-2 text-[12px]"
                  value={newSpecName} onChange={(e) => setNewSpecName(e.target.value)} />
                <div className="relative w-[76px] flex-shrink-0">
                  <input type="text" inputMode="numeric" placeholder="가로"
                    className="w-full border border-[#e0e0e0] rounded-[8px] pl-2 pr-7 py-2 text-[12px] text-right"
                    value={newSpecWidth} onChange={(e) => setNewSpecWidth(e.target.value.replace(/[^0-9]/g, ""))} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                </div>
                <span className="text-[11px] text-[#999] flex-shrink-0">×</span>
                <div className="relative w-[76px] flex-shrink-0">
                  <input type="text" inputMode="numeric" placeholder="세로"
                    className="w-full border border-[#e0e0e0] rounded-[8px] pl-2 pr-7 py-2 text-[12px] text-right"
                    value={newSpecHeight} onChange={(e) => setNewSpecHeight(e.target.value.replace(/[^0-9]/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreateSpec(); }} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                </div>
                <button onClick={handleCreateSpec}
                  className="px-3.5 py-2 bg-[#222] text-white rounded-[8px] text-[12px] whitespace-nowrap hover:bg-[#333] flex-shrink-0">추가</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 절수별 자동 계산 확인 — 직접 고친 절수 금액은 기본으로 남겨 둔다 */}
      {cutPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => closeCutPrompt(null)}>
          <div className="bg-white rounded-[12px] w-[460px] max-w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[16px] font-bold text-[#222]">절수별 금액 자동 계산</h3>
              <button onClick={() => closeCutPrompt(null)} className="text-[#999] hover:text-[#555]">
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>
            <p className="text-[13px] text-[#555] mb-4">
              {cutPrompt.fullLabel} 금액 <span className="font-medium text-[#222]">{cutPrompt.fullPrice.toLocaleString()}원</span>을
              기준으로 절수별 금액을 계산합니다.
            </p>

            {cutPrompt.plan.recalculated.length > 0 && (
              <div className="mb-4">
                <p className="text-[12px] font-medium text-[#555] mb-1.5">다시 계산</p>
                <div className="space-y-1">
                  {cutPrompt.plan.recalculated.map((r) => (
                    <div key={r.item.id} className="flex items-center justify-between px-3 py-1.5 rounded-[6px] bg-[#fafafa] text-[12px]">
                      <span className="text-[#333]">{r.label}</span>
                      <span className="text-[#888]">
                        {r.currentPrice.toLocaleString()}원 → <span className="font-medium text-[#4285F4]">{r.newPrice.toLocaleString()}원</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cutPrompt.plan.manual.length > 0 && (
              <div className="mb-4">
                <p className="text-[12px] font-medium text-[#555] mb-1">
                  직접 입력한 금액 {overwriteManualCuts ? "(덮어씀)" : "(유지)"}
                </p>
                <p className="text-[11px] text-[#888] mb-1.5">
                  이전 전지 금액으로 자동 계산한 값과 달라, 직접 고친 금액으로 보고 그대로 둡니다.
                </p>
                <div className="space-y-1">
                  {cutPrompt.plan.manual.map((r) => (
                    <div key={r.item.id} className="flex items-center justify-between px-3 py-1.5 rounded-[6px] bg-[#fafafa] text-[12px]">
                      <span className="text-[#333]">{r.label}</span>
                      {overwriteManualCuts ? (
                        <span className="text-[#888]">
                          {r.currentPrice.toLocaleString()}원 → <span className="font-medium text-[#4285F4]">{r.newPrice.toLocaleString()}원</span>
                        </span>
                      ) : (
                        <span className="text-[#888]">
                          <span className="font-medium text-[#222]">{r.currentPrice.toLocaleString()}원</span> 유지 (자동 계산 {r.newPrice.toLocaleString()}원)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div onClick={() => setOverwriteManualCuts((v) => !v)}
                  className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                  <CheckBox isEnabled={overwriteManualCuts} className="w-[18px] h-[18px]" />
                  <span className="text-[12px] text-[#555]">직접 입력한 금액도 자동 계산 값으로 덮어쓰기</span>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => closeCutPrompt(null)}
                className="px-4 py-2 text-[13px] text-[#666] hover:text-[#333]">{cutPrompt.cancelLabel}</button>
              <button onClick={() => closeCutPrompt({ overwriteManual: overwriteManualCuts })}
                className="px-4 py-2 bg-[#222] text-white text-[13px] rounded-[8px] hover:bg-[#333]">적용</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Roll Paper Sub-resource (자재별 규격 목록 + 가격) =====
// 이 자재(materialId)에 연결된 롤 용지 규격만 보여준다. 회사 규격 풀 전체가 아니다.
// - items: 이 자재의 roll-papers (연결된 규격 + 선택적 가격). hasPrice=false면 회색 '추가 전'.
// - specs: 회사 규격 풀. "+" 드롭다운에서 아직 안 붙은 규격을 고르거나 신규 생성할 때만 쓴다.
const RollPaperSection: React.FC<{ companyId: number; materialId: number; onUnpricedCountChange?: (n: number) => void }> = ({ companyId, materialId, onUnpricedCountChange }) => {
  const [items, setItems] = useState<MaterialRollPaperDto[]>([]);
  const [specs, setSpecs] = useState<RollSizeSpecDto[]>([]);
  const [loading, setLoading] = useState(true);

  // 가격 편집
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState("");
  const priceInputRef = useRef<HTMLInputElement>(null);
  // 단위 면적 편집
  const [editingUnitId, setEditingUnitId] = useState<number | null>(null);
  const [editBaseAreaSize, setEditBaseAreaSize] = useState("");
  const [editBaseAreaUnit, setEditBaseAreaUnit] = useState("SQUARE_M");
  const unitInputRef = useRef<HTMLInputElement>(null);

  // "+" 규격 추가 드롭다운
  const [showAddMenu, setShowAddMenu] = useState(false);
  // 시각화 가로 스크롤(카루셀) ref
  const visualScrollRef = useRef<HTMLDivElement>(null);
  const scrollVisual = (dir: number) => visualScrollRef.current?.scrollBy({ left: dir * 165, behavior: "smooth" });
  const [newSpecName, setNewSpecName] = useState("");
  const [newSpecWidth, setNewSpecWidth] = useState("");
  // 풀 규격 인라인 편집
  const [editingSpecId, setEditingSpecId] = useState<number | null>(null);
  const [editSpecName, setEditSpecName] = useState("");
  const [editSpecWidth, setEditSpecWidth] = useState("");

  useEffect(() => {
    if (editingPriceId !== null && priceInputRef.current) setTimeout(() => priceInputRef.current?.focus(), 0);
  }, [editingPriceId]);
  useEffect(() => {
    if (editingUnitId !== null && unitInputRef.current) setTimeout(() => unitInputRef.current?.focus(), 0);
  }, [editingUnitId]);

  const load = useCallback(async () => {
    try {
      const [rollPapersRes, specsRes] = await Promise.all([
        companyProductSystemApiService.getRollPapers(companyId, materialId),
        companyProductSystemApiService.getRollSizeSpecs(companyId),
      ]);
      setItems(rollPapersRes.data || []);
      setSpecs((specsRes.data || []).slice().sort((a, b) => a.width - b.width));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, materialId]);

  useEffect(() => { load(); }, [load]);

  // 가격 미설정 규격 수를 부모에 보고 (자재 이탈 시 확인용). 언마운트 시 0으로 리셋.
  useEffect(() => {
    onUnpricedCountChange?.(items.filter((i) => !(i.hasPrice ?? (i.price != null))).length);
  }, [items, onUnpricedCountChange]);
  useEffect(() => {
    return () => { onUnpricedCountChange?.(0); };
  }, [onUnpricedCountChange]);

  // 이 자재에 아직 연결되지 않은 풀 규격 (드롭다운 "풀에서 추가"용)
  const linkedSpecIds = new Set(items.map(i => i.rollSizeSpecId));
  const unlinkedSpecs = specs.filter(s => !linkedSpecIds.has(s.id));
  // 표시용: 폭 오름차순
  const sortedItems = items.slice().sort((a, b) => a.width - b.width);

  const hasPriceOf = (rp: MaterialRollPaperDto): boolean => rp.hasPrice ?? (rp.price != null);

  // ===== 자재-규격 연결 / 가격 =====
  const handleAddSpecToMaterial = async (spec: RollSizeSpecDto) => {
    try {
      // 가격 없이 연결만 → hasPrice=false (회색 '추가 전')
      await companyProductSystemApiService.createRollPaper(companyId, materialId, {
        rollSizeSpecId: spec.id,
      } as Partial<MaterialRollPaperDto>);
      await load();
    } catch (e: any) { alert(e?.message || "추가에 실패했습니다."); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("이 규격을 이 자재에서 제거하시겠습니까?")) return;
    try {
      await companyProductSystemApiService.deleteRollPaper(companyId, materialId, id);
      await load();
    } catch { alert("삭제에 실패했습니다."); }
  };

  const handlePriceUpdate = async (rp: MaterialRollPaperDto, newPrice: number) => {
    try {
      await companyProductSystemApiService.updateRollPaper(companyId, materialId, rp.id, {
        rollSizeSpecId: rp.rollSizeSpecId,
        baseAreaSize: rp.baseAreaSize ?? 1,
        baseAreaUnit: rp.baseAreaUnit ?? "SQUARE_M",
        paperColor: rp.paperColor,
        price: newPrice,
      } as Partial<MaterialRollPaperDto>);
      await load();
    } catch (e: any) { alert(e?.message || "가격 수정에 실패했습니다."); }
  };

  const handleUnitUpdate = async (rp: MaterialRollPaperDto, newSize: number, newUnit: string) => {
    try {
      await companyProductSystemApiService.updateRollPaper(companyId, materialId, rp.id, {
        rollSizeSpecId: rp.rollSizeSpecId,
        baseAreaSize: newSize,
        baseAreaUnit: newUnit,
        paperColor: rp.paperColor,
        // 가격 미설정 상태에서 단위만 먼저 정할 수 없으므로(백엔드 all-or-nothing) 0원으로 가격도 함께 설정
        price: rp.price ?? 0,
      } as Partial<MaterialRollPaperDto>);
      await load();
    } catch (e: any) { alert(e?.message || "단위 수정에 실패했습니다."); }
  };

  // ===== 규격 풀 CRUD =====
  const handleCreateSpec = async () => {
    const width = Number(newSpecWidth);
    if (!width || width <= 0) { alert("폭(mm)을 입력해주세요."); return; }
    const name = newSpecName.trim();
    try {
      // 같은 이름의 풀 규격이 이미 있으면 재사용(중복 생성 방지) 후 연결
      const existing = name ? specs.find(s => s.name === name) : undefined;
      if (existing) {
        if (linkedSpecIds.has(existing.id)) { alert("이미 이 자재에 추가된 규격입니다."); return; }
        await handleAddSpecToMaterial(existing);
        setNewSpecName("");
        setNewSpecWidth("");
        setShowAddMenu(false);
        return;
      }
      const res = await companyProductSystemApiService.createRollSizeSpec(companyId, {
        name: name || undefined,
        width,
      });
      setNewSpecName("");
      setNewSpecWidth("");
      // 생성 직후 이 자재에 바로 연결 (2-call)
      if (res.data) await handleAddSpecToMaterial(res.data);
      else await load();
      setShowAddMenu(false);
    } catch (e: any) { alert(e?.message || "규격 생성에 실패했습니다."); }
  };

  // 프리셋/풀 규격 선택 → 바로 추가하지 않고 아래 입력칸을 채워 확인 후 "추가"로 확정
  const handleFillFromPreset = (preset: RollSizeQuickPreset) => {
    setNewSpecName(preset.label);
    setNewSpecWidth(String(preset.width));
  };
  const handleFillFromSpec = (spec: RollSizeSpecDto) => {
    setNewSpecName(spec.name);
    setNewSpecWidth(String(spec.width));
  };

  const handleUpdateSpec = async (spec: RollSizeSpecDto) => {
    const width = Number(editSpecWidth);
    if (!width || width <= 0) { alert("폭(mm)을 입력해주세요."); return; }
    try {
      await companyProductSystemApiService.updateRollSizeSpec(companyId, spec.id, {
        name: editSpecName.trim() || undefined,
        width,
      });
      setEditingSpecId(null);
      await load();
    } catch (e: any) { alert(e?.message || "규격 수정에 실패했습니다."); }
  };

  const handleDeleteSpec = async (spec: RollSizeSpecDto) => {
    if (!window.confirm(`"${spec.name}" 규격을 목록에서 삭제하시겠습니까?`)) return;
    try {
      await companyProductSystemApiService.deleteRollSizeSpec(companyId, spec.id);
      await load();
    } catch (e: any) { alert(e?.message || "이 규격을 사용 중인 자재가 있어 삭제할 수 없습니다."); }
  };

  if (loading) return <p className="text-[13px] text-[#999] py-2">불러오는 중...</p>;

  const maxRollWidth = Math.max(1, ...sortedItems.map((i) => i.width));
  // 가격 미설정(주문 화면 미노출) 규격 개수
  const unpricedCount = sortedItems.filter((i) => !hasPriceOf(i)).length;

  // 롤 시각화 카드 (자재에 연결된 규격 1건 = rp)
  const renderRollVisual = (rp: MaterialRollPaperDto) => {
    const priced = hasPriceOf(rp);
    const isPriceEditing = editingPriceId === rp.id;
    const isUnitEditing = editingUnitId === rp.id;
    const unitLabel = BASE_AREA_UNIT_LABELS[rp.baseAreaUnit || "SQUARE_M"] || "m²";
    const visualWidth = Math.max(64, Math.round((rp.width / maxRollWidth) * 150));
    const visualHeight = 190;
    return (
      <div key={rp.id}
        className={`border rounded-[4px] bg-white relative flex flex-col items-center justify-center group flex-shrink-0 ${priced ? "border-[#ccc]" : "border-dashed border-[#ddd]"}`}
        style={{ width: visualWidth, height: visualHeight }}>
        {!priced && (
          <span className="absolute top-1 left-1 inline-flex items-center px-1.5 py-0.5 rounded-[4px] bg-[#f0f0f0] text-[#999] text-[9px] font-medium">
            비활성
          </span>
        )}
        <span className={`text-[11px] font-medium ${priced ? "text-[#555]" : "text-[#bbb]"}`}>
          {rp.rollSizeSpecName}({rp.width}mm)
        </span>
        {/* 단위 면적 */}
        {isUnitEditing ? (
          <div className="flex items-center gap-0.5 mt-1" onClick={(e) => e.stopPropagation()}>
            <input ref={unitInputRef} type="text" inputMode="decimal"
              className="w-[32px] border border-[#ccc] rounded-[3px] px-1 py-0.5 text-[10px] text-center focus:outline-none focus:border-[#999]"
              value={editBaseAreaSize}
              onChange={(e) => setEditBaseAreaSize(e.target.value.replace(/[^0-9.]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") { const s = Number(editBaseAreaSize); if (s > 0) handleUnitUpdate(rp, s, editBaseAreaUnit); setEditingUnitId(null); }
                if (e.key === "Escape") setEditingUnitId(null);
              }} />
            <select className="border border-[#ccc] rounded-[3px] px-0.5 py-0.5 text-[9px] focus:outline-none bg-white"
              value={editBaseAreaUnit} onChange={(e) => setEditBaseAreaUnit(e.target.value)}>
              {Object.entries(BASE_AREA_UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button onClick={() => { const s = Number(editBaseAreaSize); if (s > 0) handleUnitUpdate(rp, s, editBaseAreaUnit); setEditingUnitId(null); }}
              className="text-[#666] hover:text-[#333] text-[10px] font-medium">✓</button>
          </div>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); setEditingUnitId(rp.id); setEditBaseAreaSize(String(rp.baseAreaSize ?? 1)); setEditBaseAreaUnit(rp.baseAreaUnit ?? "SQUARE_M"); }}
            className="mt-1 px-2 py-0.5 bg-[#f0f0f0] rounded-[3px] text-[10px] text-[#666] hover:bg-[#e5e5e5]">
            {priced ? `${rp.baseAreaSize ?? 1}${unitLabel}` : "단위 미설정"}
          </button>
        )}
        {/* 가격 */}
        {isPriceEditing ? (
          <div className="flex items-center gap-0.5 mt-1" onClick={(e) => e.stopPropagation()}>
            <input ref={priceInputRef} type="text" inputMode="numeric"
              className="w-[70px] border border-[#ccc] rounded-[3px] px-1 py-0.5 text-[11px] text-center focus:outline-none focus:border-[#999]"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value.replace(/[^0-9]/g, ""))}
              onBlur={() => { const p = Number(editPrice); if (!isNaN(p)) handlePriceUpdate(rp, p); setEditingPriceId(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditingPriceId(null); }} />
            <span className="text-[10px] text-[#999]">원</span>
          </div>
        ) : (
          <button onClick={(e) => { e.stopPropagation(); setEditingPriceId(rp.id); setEditPrice(priced ? String(rp.price) : ""); }}
            className={`mt-1 px-2.5 py-0.5 rounded-[3px] text-[11px] font-medium ${priced ? "bg-[#f0f0f0] text-[#333] hover:bg-[#e5e5e5]" : "bg-transparent text-[#4285F4] hover:underline"}`}>
            {priced ? `${(rp.price ?? 0).toLocaleString()}원` : "가격 설정"}
          </button>
        )}
        {/* 삭제 */}
        <button onClick={(e) => { e.stopPropagation(); handleDelete(rp.id); }}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-[#ccc] hover:text-[#F24A4A] transition-opacity">
          <Trash2 className="w-[12px] h-[12px]" />
        </button>
      </div>
    );
  };

  // 테이블 행
  const renderTableRow = (rp: MaterialRollPaperDto) => {
    const priced = hasPriceOf(rp);
    const isPriceEditing = editingPriceId === rp.id;
    const isUnitEditing = editingUnitId === rp.id;
    const unitLabel = BASE_AREA_UNIT_LABELS[rp.baseAreaUnit || "SQUARE_M"] || "m²";
    return (
      <tr key={rp.id} className="border-b border-[#eee]">
        <td className={`px-3 py-2 text-[12px] font-medium ${priced ? "text-[#333]" : "text-[#bbb]"}`}>
          <span className="inline-flex items-center gap-1">
            {rp.rollSizeSpecName}
            {!priced && (
              <span title="가격 미설정 — 주문 화면에 노출되지 않습니다"
                className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] bg-[#f0f0f0] text-[#999] text-[10px] font-medium cursor-help">비활성</span>
            )}
          </span>
        </td>
        <td className={`px-3 py-2 text-[12px] text-center ${priced ? "text-[#666]" : "text-[#bbb]"}`}>{rp.width}</td>
        {/* 단위 면적 */}
        <td className="px-3 py-2 text-center">
          {isUnitEditing ? (
            <div className="flex items-center justify-center gap-1">
              <input ref={unitInputRef} type="text" inputMode="decimal"
                className="w-[40px] border border-[#ccc] rounded-[3px] px-1 py-0.5 text-[11px] text-center focus:outline-none focus:border-[#999]"
                value={editBaseAreaSize} onChange={(e) => setEditBaseAreaSize(e.target.value.replace(/[^0-9.]/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") { const s = Number(editBaseAreaSize); if (s > 0) handleUnitUpdate(rp, s, editBaseAreaUnit); setEditingUnitId(null); } if (e.key === "Escape") setEditingUnitId(null); }}
                onClick={(e) => e.stopPropagation()} />
              <select className="border border-[#ccc] rounded-[3px] px-1 py-0.5 text-[11px] focus:outline-none bg-white"
                value={editBaseAreaUnit} onChange={(e) => setEditBaseAreaUnit(e.target.value)}>
                {Object.entries(BASE_AREA_UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={() => { const s = Number(editBaseAreaSize); if (s > 0) handleUnitUpdate(rp, s, editBaseAreaUnit); setEditingUnitId(null); }}
                className="text-[#666] hover:text-[#333] text-[11px] font-medium">✓</button>
            </div>
          ) : priced ? (
            <button onClick={(e) => { e.stopPropagation(); setEditingUnitId(rp.id); setEditBaseAreaSize(String(rp.baseAreaSize ?? 1)); setEditBaseAreaUnit(rp.baseAreaUnit ?? "SQUARE_M"); }}
              className="px-2 py-0.5 bg-[#f0f0f0] rounded-[3px] text-[11px] text-[#666] hover:bg-[#e5e5e5]">
              {rp.baseAreaSize ?? 1}{unitLabel}
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); setEditingUnitId(rp.id); setEditBaseAreaSize(String(rp.baseAreaSize ?? 1)); setEditBaseAreaUnit(rp.baseAreaUnit ?? "SQUARE_M"); }}
              className="text-[12px] text-[#4285F4] hover:underline">단위 설정</button>
          )}
        </td>
        {/* 가격 */}
        <td className="px-3 py-2 text-center">
          {isPriceEditing ? (
            <input ref={priceInputRef} type="text" inputMode="numeric"
              className="w-[80px] border border-[#ccc] rounded-[3px] px-1.5 py-0.5 text-[12px] text-center focus:outline-none focus:border-[#999]"
              value={editPrice} onChange={(e) => setEditPrice(e.target.value.replace(/[^0-9]/g, ""))}
              onBlur={() => { const p = Number(editPrice); if (!isNaN(p)) handlePriceUpdate(rp, p); setEditingPriceId(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditingPriceId(null); }}
              onClick={(e) => e.stopPropagation()} />
          ) : priced ? (
            <button onClick={(e) => { e.stopPropagation(); setEditingPriceId(rp.id); setEditPrice(String(rp.price)); }}
              className="px-2 py-0.5 bg-[#f0f0f0] rounded-[3px] text-[12px] font-medium text-[#333] hover:bg-[#e5e5e5]">
              {(rp.price ?? 0).toLocaleString()}원
            </button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); setEditingPriceId(rp.id); setEditPrice(""); }}
              className="text-[12px] text-[#4285F4] hover:underline">가격 설정</button>
          )}
        </td>
        {/* 삭제 */}
        <td className="px-3 py-2 text-center">
          <button onClick={() => handleDelete(rp.id)} className="text-[#ccc] hover:text-[#F24A4A]">
            <Trash2 className="w-[13px] h-[13px]" />
          </button>
        </td>
      </tr>
    );
  };

  // 안내 문구 + "+" 규격 추가 버튼 (표 바로 아래에 렌더)
  const renderAddArea = () => (
    <>
      {/* 가격 미설정 요약 배너 (표 아래) */}
      {unpricedCount > 0 && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[#EBF3FF] border border-[#4285F4]/20 text-[12px] text-[#4285F4]">
          <AlertCircle className="w-[15px] h-[15px] flex-shrink-0" />
          <span>가격이 설정되지 않은 규격 <b>{unpricedCount}개</b>는 고객 주문 화면에 노출되지 않습니다. 가격을 설정해주세요.</span>
        </div>
      )}
      <p className="mt-2 text-[11px] text-[#999]">
        롤 용지 가격은 <span className="text-[#666]">단위 면적(m²)당</span> 가격으로 설정됩니다.
      </p>
      <div className="mt-2 flex justify-center">
        <button onClick={() => setShowAddMenu(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 border border-[#e0e0e0] rounded-[8px] text-[12px] text-[#4285F4] hover:border-[#4285F4] hover:bg-[#f8faff]">
          <Plus className="w-[14px] h-[14px]" /> 규격 추가
        </button>
      </div>
    </>
  );

  return (
    <div>
      {sortedItems.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-[13px] text-[#999]">이 자재에 추가된 롤 용지 규격이 없습니다.</p>
          {renderAddArea()}
        </div>
      ) : (
        <div className="flex gap-10 items-start py-4">
          {/* 시각화 (좌) - 양옆 < > 화살표 카루셀(가로 스크롤) */}
          <div className="flex-shrink-0 flex items-center gap-1.5">
            {sortedItems.length > 3 && (
              <button onClick={() => scrollVisual(-1)} title="이전"
                className="flex-shrink-0 w-[28px] h-[28px] flex items-center justify-center rounded-full border border-[#e0e0e0] bg-white text-[#888] hover:border-[#4285F4] hover:text-[#4285F4] shadow-sm">
                <ChevronLeft className="w-[16px] h-[16px]" />
              </button>
            )}
            <div ref={visualScrollRef} className="flex items-end gap-3 overflow-x-auto max-w-[380px] pb-1 scroll-smooth">
              {sortedItems.map((rp) => renderRollVisual(rp))}
            </div>
            {sortedItems.length > 3 && (
              <button onClick={() => scrollVisual(1)} title="다음"
                className="flex-shrink-0 w-[28px] h-[28px] flex items-center justify-center rounded-full border border-[#e0e0e0] bg-white text-[#888] hover:border-[#4285F4] hover:text-[#4285F4] shadow-sm">
                <ChevronRight className="w-[16px] h-[16px]" />
              </button>
            )}
          </div>
          {/* 테이블 (우) */}
          <div className="flex-1 min-w-0">
            <div className="border border-[#ccc] rounded-[4px] overflow-hidden bg-white">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#eee]">
                    <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-left">규격</th>
                    <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">사이즈(mm)</th>
                    <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">단위 면적</th>
                    <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center">가격</th>
                    <th className="px-3 py-2 text-[11px] font-medium text-[#888] text-center w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>{sortedItems.map((rp) => renderTableRow(rp))}</tbody>
              </table>
            </div>
            {/* 규격 추가: 표 바로 아래 */}
            {renderAddArea()}
          </div>
        </div>
      )}

      {showAddMenu && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowAddMenu(false); setEditingSpecId(null); }}>
          <div className="bg-white rounded-[12px] w-[420px] max-w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold text-[#222]">롤 용지 규격 추가</h3>
              <button onClick={() => { setShowAddMenu(false); setEditingSpecId(null); }}
                className="text-[#999] hover:text-[#555]">
                <X className="w-[18px] h-[18px]" />
              </button>
            </div>

            {/* 풀에서 추가 */}
            {unlinkedSpecs.length > 0 && (
              <div className="mb-5">
                <p className="text-[12px] font-medium text-[#888] mb-2">기존 규격 불러오기</p>
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                  {unlinkedSpecs.map((s) => (
                    <div key={s.id} className="flex items-center gap-1.5">
                      {editingSpecId === s.id ? (
                        <>
                          <input type="text" autoFocus placeholder="이름"
                            className="w-[96px] border border-[#ccc] rounded-[6px] px-2 py-1.5 text-[12px]"
                            value={editSpecName} onChange={(e) => setEditSpecName(e.target.value)} />
                          <div className="relative w-[88px]">
                            <input type="text" inputMode="numeric"
                              className="w-full border border-[#ccc] rounded-[6px] pl-2 pr-7 py-1.5 text-[12px] text-right"
                              value={editSpecWidth} onChange={(e) => setEditSpecWidth(e.target.value.replace(/[^0-9]/g, ""))} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                          </div>
                          <button onClick={() => handleUpdateSpec(s)} className="text-[#4285F4] text-[12px] px-1">저장</button>
                          <button onClick={() => setEditingSpecId(null)} className="text-[#999] text-[12px] px-1">취소</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleFillFromSpec(s)}
                            className="flex-1 flex items-center justify-between px-2.5 py-2 rounded-[8px] text-[13px] text-[#333] hover:bg-[#f5f5f5] border border-[#f0f0f0]">
                            <span>{s.name}<span className="text-[#999] ml-1">({s.width}mm)</span></span>
                            <span className="text-[12px] text-[#4285F4]">불러오기</span>
                          </button>
                          <button onClick={() => { setEditingSpecId(s.id); setEditSpecName(s.name); setEditSpecWidth(String(s.width)); }}
                            className="text-[#bbb] hover:text-[#666] text-[11px] px-1">수정</button>
                          <button onClick={() => handleDeleteSpec(s)} className="text-[#ccc] hover:text-[#F24A4A]">
                            <Trash2 className="w-[13px] h-[13px]" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 새 규격 만들기 */}
            <div>
              <p className="text-[12px] font-medium text-[#888] mb-2">새 규격 만들기</p>
              <p className="text-[11px] text-[#aaa] mb-2">아래 프리셋을 누르면 입력칸에 채워집니다. 확인 후 <span className="text-[#666]">추가</span>를 눌러주세요.</p>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {ROLL_SIZE_QUICK_PRESETS.map((p) => (
                  <button key={p.label} type="button" onClick={() => handleFillFromPreset(p)}
                    className="px-2.5 py-1.5 border border-[#e0e0e0] rounded-[6px] text-[12px] text-[#666] hover:border-[#4285F4] hover:text-[#4285F4]">
                    {p.label}({p.width}mm)
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 items-center">
                <input type="text" placeholder="이름 (선택, 비우면 자동생성)"
                  className="flex-1 border border-[#e0e0e0] rounded-[8px] px-2.5 py-2 text-[12px]"
                  value={newSpecName} onChange={(e) => setNewSpecName(e.target.value)} />
                <div className="relative w-[88px]">
                  <input type="text" inputMode="numeric" placeholder="폭"
                    className="w-full border border-[#e0e0e0] rounded-[8px] pl-2 pr-7 py-2 text-[12px] text-right"
                    value={newSpecWidth} onChange={(e) => setNewSpecWidth(e.target.value.replace(/[^0-9]/g, ""))}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreateSpec(); }} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#999] pointer-events-none">mm</span>
                </div>
                <button onClick={handleCreateSpec}
                  className="px-3.5 py-2 bg-[#222] text-white rounded-[8px] text-[12px] whitespace-nowrap hover:bg-[#333]">추가</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// ===== CMYK to Hex 변환 유틸리티 =====
const cmykToHex = (c: number, m: number, y: number, k: number): string => {
  const r = Math.round(255 * (1 - c / 100) * (1 - k / 100));
  const g = Math.round(255 * (1 - m / 100) * (1 - k / 100));
  const b = Math.round(255 * (1 - y / 100) * (1 - k / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
};

// Hex to CMYK 변환 (근사값)
const hexToCmyk = (hex: string): { c: number; m: number; y: number; k: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { c: 0, m: 0, y: 0, k: 0 };

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const k = 1 - Math.max(r, g, b);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 };

  const c = Math.round((1 - r - k) / (1 - k) * 100);
  const m = Math.round((1 - g - k) / (1 - k) * 100);
  const y = Math.round((1 - b - k) / (1 - k) * 100);

  return { c, m, y, k: Math.round(k * 100) };
};

// ===== Ink Tab Content (잉크 탭 전용 - 상단: 인쇄구성 횡스크롤, 하단: 잉크 그리드) =====
interface PrintConfigWithMaterial {
  material: MaterialDto;
  config: InkConfigDto | null;
}

const InkTabContent: React.FC<{
  companyId: number;
  inkConfigMaterials: MaterialDto[];
  inkMaterials: MaterialDto[];
  onDeleteMaterial: (id: number) => void;
  onRefresh: () => void;
}> = ({ companyId, inkConfigMaterials, inkMaterials, onDeleteMaterial, onRefresh }) => {
  // 인쇄 구성 데이터 (material + inkConfig)
  const [printConfigs, setPrintConfigs] = useState<PrintConfigWithMaterial[]>([]);
  const [configsLoading, setConfigsLoading] = useState(true);

  // 전체 잉크 데이터
  const [allInks, setAllInks] = useState<(InkDto & { materialId: number })[]>([]);
  const [inksLoading, setInksLoading] = useState(true);

  // 인쇄구성 편집 (하단 확장 영역)
  const [expandedConfigId, setExpandedConfigId] = useState<number | null>(null);
  const [selectedInkIds, setSelectedInkIds] = useState<Set<number>>(new Set());
  const [editPriceTiers, setEditPriceTiers] = useState<{ minQuantity: number; price: number }[]>([]);

  // 인쇄 구성 로드 (각 INK_CONFIG material에 대해)
  const loadPrintConfigs = useCallback(async () => {
    setConfigsLoading(true);
    try {
      const results: PrintConfigWithMaterial[] = await Promise.all(
        inkConfigMaterials.map(async (material) => {
          try {
            const res = await companyProductSystemApiService.getInkConfigs(companyId, material.id);
            // 1:1 관계이므로 첫 번째만
            return { material, config: res.data?.[0] || null };
          } catch {
            return { material, config: null };
          }
        })
      );
      setPrintConfigs(results);
    } finally {
      setConfigsLoading(false);
    }
  }, [companyId, inkConfigMaterials]);

  // 전체 잉크 로드 (각 INK material에 대해)
  const loadAllInks = useCallback(async () => {
    setInksLoading(true);
    try {
      const results = await Promise.all(
        inkMaterials.map(async (material) => {
          try {
            const res = await companyProductSystemApiService.getInks(companyId, material.id);
            return (res.data || []).map(ink => ({ ...ink, materialId: material.id }));
          } catch {
            return [];
          }
        })
      );
      setAllInks(results.flat());
    } finally {
      setInksLoading(false);
    }
  }, [companyId, inkMaterials]);

  useEffect(() => { loadPrintConfigs(); }, [loadPrintConfigs]);
  useEffect(() => { loadAllInks(); }, [loadAllInks]);

  // 인쇄구성 삭제 (Material 삭제). 확인은 여기서 한 번만 받고, onDeleteMaterial 은 확인 없이 삭제한다
  const handleDeletePrintConfig = async (materialId: number) => {
    if (!window.confirm("이 인쇄 구성을 삭제하시겠습니까?")) return;
    onDeleteMaterial(materialId);
  };

  // 잉크 삭제
  const handleDeleteInk = async (ink: InkDto & { materialId: number }) => {
    if (!window.confirm("이 잉크를 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemApiService.deleteInk(companyId, ink.materialId, ink.id);
      await loadAllInks();
    } catch (e: any) {
      // 409: 인쇄 구성에서 사용 중. 어느 인쇄 구성인지 알려 줘야 사용자가 풀 수 있다
      const inkConfigs: { id: number; name: string }[] = e?.inkConfigs || [];
      if (inkConfigs.length > 0) {
        alert(
          `${e.message}\n사용 중인 인쇄 구성: ${inkConfigs.map((c) => c.name).join(", ")}\n` +
          "먼저 해당 인쇄 구성에서 이 잉크를 빼 주세요."
        );
      } else {
        alert(e?.message || "삭제에 실패했습니다.");
      }
    }
  };

  // 잉크 수정 상태
  const [editingInk, setEditingInk] = useState<(InkDto & { materialId: number }) | null>(null);
  const [editInkName, setEditInkName] = useState("");
  const [editInkCmyk, setEditInkCmyk] = useState({ c: 0, m: 0, y: 0, k: 0 });
  const [showEditColorPicker, setShowEditColorPicker] = useState(false);
  const editInkColor = cmykToHex(editInkCmyk.c, editInkCmyk.m, editInkCmyk.y, editInkCmyk.k);

  // 잉크 수정 시작
  const startEditInk = (ink: InkDto & { materialId: number }) => {
    setEditingInk(ink);
    setEditInkName(ink.name);
    if (ink.inkColor) {
      setEditInkCmyk(hexToCmyk(ink.inkColor));
    } else {
      setEditInkCmyk({ c: 0, m: 0, y: 0, k: 0 });
    }
  };

  // 잉크 수정 저장
  const saveEditInk = async () => {
    if (!editingInk || !editInkName.trim()) return;
    try {
      await companyProductSystemApiService.updateInk(companyId, editingInk.materialId, editingInk.id, {
        name: editInkName.trim(),
        inkColor: editInkColor,
      } as any);
      await loadAllInks();
      setEditingInk(null);
    } catch {
      alert("수정에 실패했습니다.");
    }
  };

  // 인쇄구성 확장 토글
  const toggleExpandConfig = (pc: PrintConfigWithMaterial) => {
    if (expandedConfigId === pc.material.id) {
      setExpandedConfigId(null);
    } else {
      setExpandedConfigId(pc.material.id);
      setSelectedInkIds(new Set(pc.config?.inks.map(i => i.id) || []));
      const tiers = pc.config?.priceTiers.map(pt => ({
        minQuantity: pt.minQuantity,
        price: typeof pt.price === 'number' ? pt.price : Number(pt.price),
      })) || [{ minQuantity: 1, price: 0 }];
      setEditPriceTiers(tiers);
    }
  };

  // 현재 확장된 인쇄 구성
  const expandedConfig = printConfigs.find(pc => pc.material.id === expandedConfigId);

  // 저장 중 상태
  const [isSaving, setIsSaving] = useState(false);

  // 즉시 저장 (잉크 또는 가격 변경 시)
  const saveConfigImmediate = async (inkIds: Set<number>, priceTiers: { minQuantity: number; price: number }[]) => {
    if (!expandedConfig?.config) return;
    const hasBaseTier = priceTiers.some(t => t.minQuantity === 1);
    if (!hasBaseTier) return; // 기본 구간 없으면 저장하지 않음

    setIsSaving(true);
    try {
      await companyProductSystemApiService.updateInkConfig(
        companyId,
        expandedConfig.material.id,
        expandedConfig.config.id,
        {
          name: expandedConfig.config.name,
          inkIds: Array.from(inkIds),
          priceTiers: priceTiers.map(pt => ({
            minQuantity: pt.minQuantity,
            price: pt.price,
          })),
        } as any
      );
      await loadPrintConfigs();
    } catch {
      // 조용히 실패 (UI에서 즉시 반영되어 있으므로)
    } finally {
      setIsSaving(false);
    }
  };

  // 잉크 토글 (즉시 저장)
  const toggleInk = (inkId: number) => {
    const newSet = new Set(selectedInkIds);
    if (newSet.has(inkId)) {
      newSet.delete(inkId);
    } else {
      newSet.add(inkId);
    }
    setSelectedInkIds(newSet);
    saveConfigImmediate(newSet, editPriceTiers);
  };

  // 가격 구간 추가 (즉시 저장)
  const addPriceTier = () => {
    const lastTier = editPriceTiers[editPriceTiers.length - 1];
    const nextMinQty = lastTier ? lastTier.minQuantity + 100 : 1;
    const newTiers = [...editPriceTiers, { minQuantity: nextMinQty, price: 0 }];
    setEditPriceTiers(newTiers);
    saveConfigImmediate(selectedInkIds, newTiers);
  };

  // 가격 구간 삭제 (즉시 저장)
  const removePriceTier = (index: number) => {
    if (editPriceTiers.length <= 1) return;
    const newTiers = editPriceTiers.filter((_, i) => i !== index);
    setEditPriceTiers(newTiers);
    saveConfigImmediate(selectedInkIds, newTiers);
  };

  // 가격 구간 수정 (상태만 업데이트)
  const updatePriceTier = (index: number, field: 'minQuantity' | 'price', value: number) => {
    const newTiers = [...editPriceTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setEditPriceTiers(newTiers);
  };

  // 가격 구간 blur 시 저장
  const handlePriceTierBlur = () => {
    saveConfigImmediate(selectedInkIds, editPriceTiers);
  };

  const isLoading = configsLoading || inksLoading;

  return (
    <div className="space-y-8">
      {/* 상단: 인쇄 구성 (횡스크롤 카드 + 하단 확장) */}
      <div className="space-y-4">
        <h3 className="text-[15px] font-bold text-[#222] flex items-center gap-2">
          <span className="w-2 h-2 bg-[#4285F4] rounded-full"></span>
          인쇄 구성
        </h3>
        {configsLoading ? (
          <p className="text-[13px] text-[#999] py-4">불러오는 중...</p>
        ) : printConfigs.length === 0 ? (
          <p className="text-[13px] text-[#bbb] py-4">등록된 인쇄 구성이 없습니다. 우측 상단의 "자재 추가" 버튼으로 추가하세요.</p>
        ) : (
          <>
            {/* 카드 그리드 */}
            <div className="flex flex-wrap gap-4">
              {printConfigs.map((pc) => {
                const isExpanded = expandedConfigId === pc.material.id;
                return (
                  <div
                    key={pc.material.id}
                    onClick={() => toggleExpandConfig(pc)}
                    className={`w-[200px] p-4 border rounded-[12px] bg-white cursor-pointer transition-all hover:shadow-md ${
                      isExpanded ? "border-[#4285F4] ring-2 ring-[#4285F4]/20" : "border-[#e0e0e0]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[14px] font-medium text-[#333]">{pc.material.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeletePrintConfig(pc.material.id); }}
                        className="text-[#ccc] hover:text-[#F24A4A]"
                      >
                        <Trash2 className="w-[14px] h-[14px]" />
                      </button>
                    </div>

                    {/* 연결된 잉크 표시 */}
                    {pc.config && pc.config.inks.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {pc.config.inks.slice(0, 4).map((ink) => (
                          <span
                            key={ink.id}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#E8EFFF] text-[#4285F4] rounded-full"
                          >
                            {ink.inkColor && (
                              <span
                                className="w-[6px] h-[6px] rounded-full border border-[#ccc]"
                                style={{ backgroundColor: ink.inkColor }}
                              />
                            )}
                            {ink.name}
                          </span>
                        ))}
                        {pc.config.inks.length > 4 && (
                          <span className="text-[10px] text-[#888]">+{pc.config.inks.length - 4}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[#bbb] mb-2">잉크 없음</p>
                    )}

                    {/* 가격 구간 요약 */}
                    {pc.config && pc.config.priceTiers.length > 0 && (
                      <p className="text-[10px] text-[#888]">
                        {pc.config.priceTiers[0].minQuantity}매~ {pc.config.priceTiers[0].price.toLocaleString()}원
                        {pc.config.priceTiers.length > 1 && ` 외 ${pc.config.priceTiers.length - 1}개 구간`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 확장된 편집 영역 */}
            {expandedConfig && (
              <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-[12px] overflow-hidden">
                {/* 헤더 */}
                <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-[#eee]">
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-semibold text-[#222]">{expandedConfig.material.name}</span>
                    {isSaving && (
                      <span className="px-2 py-0.5 text-[10px] bg-[#E8EFFF] text-[#4285F4] rounded-full">저장 중...</span>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedConfigId(null)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#f0f0f0] text-[#999] hover:text-[#666] transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* 컨텐츠 */}
                <div className="p-5">
                  <div className="flex gap-8">
                    {/* 잉크 선택 */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[12px] font-semibold text-[#555] mb-3">잉크 선택</h4>
                      {allInks.length === 0 ? (
                        <p className="text-[12px] text-[#bbb]">등록된 잉크가 없습니다.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {allInks.map((ink) => {
                            const isSelected = selectedInkIds.has(ink.id);
                            const inkColor = ink.inkColor || '#888888';
                            return (
                              <button
                                key={ink.id}
                                onClick={() => toggleInk(ink.id)}
                                disabled={isSaving}
                                className={`group flex flex-col items-center gap-1.5 px-3 py-2 rounded-[8px] transition-all ${
                                  isSelected
                                    ? "bg-[#f0f4ff] border-2 border-[#4285F4]"
                                    : "bg-white border border-[#e5e5e5] hover:border-[#c0c0c0]"
                                } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                                title={ink.name}
                              >
                                {/* 잉크 색상 원형 */}
                                <div
                                  className="w-6 h-6 rounded-full border-2"
                                  style={{
                                    backgroundColor: inkColor,
                                    borderColor: isSelected ? '#4285F4' : '#d0d0d0'
                                  }}
                                />
                                {/* 잉크 이름 */}
                                <span className={`text-[10px] font-medium ${
                                  isSelected ? "text-[#4285F4]" : "text-[#555]"
                                }`}>
                                  {ink.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* 구분선 */}
                    <div className="w-px bg-[#e8e8e8]" />

                    {/* 가격 구간 */}
                    <div className="w-[320px] flex-shrink-0">
                      <h4 className="text-[12px] font-semibold text-[#555] mb-3">가격 구간</h4>
                      <div className="bg-white rounded-[8px] border border-[#e8e8e8] overflow-hidden">
                        {/* 테이블 헤더 */}
                        <div className="flex items-center px-3 py-2 bg-[#f8f8f8] border-b border-[#eee] text-[11px] font-medium text-[#888]">
                          <div className="w-24">최소 수량</div>
                          <div className="flex-1 text-right">단가 (원)</div>
                          <div className="w-8" />
                        </div>
                        {/* 테이블 바디 */}
                        <div className="divide-y divide-[#f0f0f0]">
                          {editPriceTiers.map((tier, index) => (
                            <div key={index} className="flex items-center px-3 py-2">
                              <div className="w-24">
                                <input
                                  type="number"
                                  value={tier.minQuantity || ""}
                                  onChange={(e) => updatePriceTier(index, 'minQuantity', Number(e.target.value))}
                                  onBlur={handlePriceTierBlur}
                                  disabled={isSaving}
                                  className="w-16 px-2 py-1 text-[12px] border border-[#e0e0e0] rounded-[4px] focus:border-[#4285F4] focus:outline-none disabled:opacity-50"
                                />
                              </div>
                              <div className="flex-1 flex justify-end">
                                <input
                                  type="number"
                                  value={tier.price || ""}
                                  onChange={(e) => updatePriceTier(index, 'price', Number(e.target.value))}
                                  onBlur={handlePriceTierBlur}
                                  disabled={isSaving}
                                  className="w-24 px-2 py-1 text-[12px] text-right border border-[#e0e0e0] rounded-[4px] focus:border-[#4285F4] focus:outline-none disabled:opacity-50"
                                />
                              </div>
                              <div className="w-8 flex justify-center">
                                {editPriceTiers.length > 1 && (
                                  <button
                                    onClick={() => removePriceTier(index)}
                                    disabled={isSaving}
                                    className="p-1 text-[#ccc] hover:text-[#F24A4A] transition-colors disabled:opacity-50"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* 추가 버튼 */}
                        <div className="px-3 py-2 border-t border-[#eee]">
                          <button
                            onClick={addPriceTier}
                            disabled={isSaving}
                            className="text-[11px] font-medium text-[#4285F4] hover:text-[#3367D6] disabled:opacity-50"
                          >
                            + 구간 추가
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 하단: 잉크 목록 (그리드) */}
      <div>
        <h3 className="text-[15px] font-bold text-[#222] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 bg-[#4285F4] rounded-full"></span>
          잉크 목록
        </h3>
        {inksLoading ? (
          <p className="text-[13px] text-[#999] py-4">불러오는 중...</p>
        ) : allInks.length === 0 ? (
          <p className="text-[13px] text-[#bbb] py-4">등록된 잉크가 없습니다. 우측 상단의 "자재 추가" 버튼으로 추가하세요.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {allInks.map((ink) => (
              <div
                key={ink.id}
                onClick={() => startEditInk(ink)}
                className="flex items-center gap-3 p-3 border border-[#e0e0e0] rounded-[8px] bg-white cursor-pointer hover:border-[#4285F4] hover:shadow-sm transition-all group"
              >
                {ink.inkColor ? (
                  <span
                    className="w-[24px] h-[24px] rounded-full border border-[#ddd] flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: ink.inkColor }}
                  />
                ) : (
                  <span className="w-[24px] h-[24px] rounded-full border-2 border-dashed border-[#ddd] flex-shrink-0" />
                )}
                <span className="text-[13px] text-[#333] flex-grow truncate">{ink.name}</span>
                {ink.isDeletable && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteInk(ink); }}
                    className="text-[#ccc] hover:text-[#F24A4A] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-[14px] h-[14px]" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 잉크 수정 모달 */}
      {editingInk && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[1000]" onClick={() => setEditingInk(null)} />
          <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] shadow-2xl w-[320px] p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[15px] font-bold text-[#222]">잉크 수정</h3>
                <button onClick={() => setEditingInk(null)} className="text-[#999] hover:text-[#666]">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* 잉크 이름 */}
              <div className="mb-3">
                <label className="block text-[12px] font-medium text-[#555] mb-1">잉크 이름</label>
                <input
                  type="text"
                  value={editInkName}
                  onChange={(e) => setEditInkName(e.target.value)}
                  className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none"
                />
              </div>

              {/* 색상 선택 */}
              <div className="mb-4">
                <label className="block text-[12px] font-medium text-[#555] mb-2">잉크 색상</label>
                {/* 색상 미리보기 + CMYK 입력 (2x2 그리드) */}
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowEditColorPicker(!showEditColorPicker)}
                      className="w-[72px] h-[72px] rounded-[8px] border-2 border-[#e0e0e0] cursor-pointer hover:border-[#bbb] transition-colors"
                      style={{ backgroundColor: editInkColor }}
                    />
                    {/* Color Picker 드롭다운 */}
                    {showEditColorPicker && (
                      <>
                        <div className="fixed inset-0 z-[55]" onClick={() => setShowEditColorPicker(false)} />
                        <div className="absolute top-[80px] left-0 z-[56] bg-white rounded-[12px] shadow-xl border border-[#e0e0e0] p-3">
                          <div className="flex justify-end mb-2">
                            <button onClick={() => setShowEditColorPicker(false)} className="text-[#888] hover:text-[#222]">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <style>{`
                            .edit-color-picker .react-colorful {
                              width: 200px !important;
                              height: auto !important;
                            }
                            .edit-color-picker .react-colorful__saturation {
                              border-radius: 6px !important;
                              height: 150px !important;
                            }
                            .edit-color-picker .react-colorful__hue {
                              height: 12px !important;
                              border-radius: 6px !important;
                              margin-top: 10px !important;
                            }
                            .edit-color-picker .react-colorful__pointer {
                              width: 16px !important;
                              height: 16px !important;
                              border-width: 2px !important;
                            }
                          `}</style>
                          <div className="edit-color-picker">
                            <HexColorPicker color={editInkColor} onChange={(color) => setEditInkCmyk(hexToCmyk(color))} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {['c', 'm', 'y', 'k'].map((key) => (
                      <div key={key} className="flex items-center gap-1.5">
                        <span className="text-[12px] font-semibold text-[#555] uppercase w-4">{key}</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={editInkCmyk[key as keyof typeof editInkCmyk]}
                          onChange={(e) => setEditInkCmyk(prev => ({ ...prev, [key]: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                          className="w-16 border border-[#e0e0e0] rounded-[6px] px-2 py-1.5 text-[13px] text-center focus:border-[#4285F4] focus:outline-none"
                        />
                        <span className="text-[11px] text-[#888]">%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingInk(null)} className="px-4 py-2 text-[13px] text-[#666] hover:bg-[#f5f5f5] rounded-[6px]">
                  취소
                </button>
                <button
                  onClick={saveEditInk}
                  disabled={!editInkName.trim()}
                  className="px-5 py-2 text-[13px] font-medium text-white bg-[#222] rounded-[6px] hover:bg-[#333] disabled:bg-[#ccc]"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

// ===== Ink Management Section (기존 - 개별 material용, 현재 사용되지 않음) =====
const InkManagementSection: React.FC<{ companyId: number; materialId: number }> = ({ companyId, materialId }) => {
  return <p className="text-[13px] text-[#999]">이 자재의 상세 정보는 잉크 탭에서 확인하세요.</p>;
};

// ===== Other Material Sub-resource =====
// ===== Other Material Sub-resource (1:1 관계, 인라인 편집) =====
const OtherMaterialSection: React.FC<{ companyId: number; materialId: number }> = ({ companyId, materialId }) => {
  const [item, setItem] = useState<OtherMaterialDto | null>(null);
  const [loading, setLoading] = useState(true);
  // 편집 상태 (항상 표시, 값만 추적)
  const [price, setPrice] = useState("0");

  const load = useCallback(async () => {
    try {
      const res = await companyProductSystemApiService.getOtherMaterials(companyId, materialId);
      const data = res.data?.[0] || null;
      setItem(data);
      if (data) {
        setPrice(String(data.price || 0));
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [companyId, materialId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!item) return;
    const newPrice = Number(price) || 0;
    try {
      await companyProductSystemApiService.updateOtherMaterial(companyId, materialId, item.id, {
        name: item.name,
        price: newPrice,
      } as any);
      await load();
    } catch { alert("가격 수정에 실패했습니다."); }
  };

  if (loading) return <p className="text-[13px] text-[#999] py-2">불러오는 중...</p>;
  if (!item) return <p className="text-[13px] text-[#bbb] py-2">기타 자재 정보가 없습니다.</p>;

  return (
    <div className="pl-4 border-l-2 border-[#E8EFFF]">
      <div className="p-4 bg-[#fafafa] rounded-[8px]">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] text-[#888]">가격:</span>
            <input
              type="number"
              className="w-[100px] border border-[#e0e0e0] rounded-[4px] px-2 py-1 text-[12px] text-center focus:border-[#4285F4] focus:outline-none"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            />
            <span className="text-[12px] text-[#555]">원</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Sub-resource Dispatcher =====
const MaterialSubResources: React.FC<{ companyId: number; material: MaterialDto; onRollUnpricedChange?: (n: number) => void }> = ({ companyId, material, onRollUnpricedChange }) => {
  switch (material.type) {
    case "SHEET_PAPER": return <SheetPaperSection companyId={companyId} materialId={material.id} />;
    case "ROLL_PAPER": return <RollPaperSection companyId={companyId} materialId={material.id} onUnpricedCountChange={onRollUnpricedChange} />;
    case "INK_CONFIG": return <InkManagementSection companyId={companyId} materialId={material.id} />;
    case "OTHER": return <OtherMaterialSection companyId={companyId} materialId={material.id} />;
    default: return null;
  }
};

// 자재 삭제가 막혔을 때(409) 서버가 돌려주는 사용 중 상품
interface ProductInUse {
  id: number;
  name: string;
  thumbnail?: string;
}

// ===== Main Page =====
const MaterialManagementPage: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [paperSpecs, setPaperSpecs] = useState<PaperSpecDto[]>([]);
  const [typeFilter, setTypeFilter] = useState("SHEET_PAPER");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // 펼친 롤 용지 자재의 가격 미설정 규격 수 (이탈 확인용)
  const rollUnpricedRef = useRef(0);
  const handleRollUnpricedChange = useCallback((n: number) => { rollUnpricedRef.current = n; }, []);
  // 펼친 자재를 벗어나기 전 확인 (가격 미설정 규격이 남아있으면). 예=이동, 아니오=취소
  const confirmLeaveExpanded = (): boolean => {
    if (expandedId !== null && rollUnpricedRef.current > 0) {
      return window.confirm("가격이 설정되지 않은 규격은 고객 주문 화면에 노출되지 않습니다.\n그래도 나가시겠습니까?");
    }
    return true;
  };
  const guardedSetExpanded = (nextId: number | null) => {
    if (!confirmLeaveExpanded()) return;
    setExpandedId(nextId);
  };

  // 잉크 탭용 상태 (INK_CONFIG + INK 분리)
  const [inkConfigMaterials, setInkConfigMaterials] = useState<MaterialDto[]>([]);
  const [inkMaterials, setInkMaterials] = useState<MaterialDto[]>([]);

  // Material CRUD
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("SHEET_PAPER");
  const [newPaperSpecId, setNewPaperSpecId] = useState<number | undefined>(undefined);
  // 잉크 추가용 CMYK 색상
  const [newInkCmyk, setNewInkCmyk] = useState({ c: 0, m: 0, y: 0, k: 0 });
  const [showNewColorPicker, setShowNewColorPicker] = useState(false);
  const newInkColor = cmykToHex(newInkCmyk.c, newInkCmyk.m, newInkCmyk.y, newInkCmyk.k);

  // 다단계 자재 추가: 1 = 자재별 첫 스텝, 2 = 자재별 두번째 스텝 (용지 평량 선택)
  const [addStep, setAddStep] = useState<1 | 2>(1);
  const [selectedPaperBaseName, setSelectedPaperBaseName] = useState<string>("");

  // 커스텀 용지 추가용 상태
  const [isCustomPaper, setIsCustomPaper] = useState(false);
  const [customPaperName, setCustomPaperName] = useState("");
  const [customGrammage, setCustomGrammage] = useState<number>(0);
  const [customIsCoated, setCustomIsCoated] = useState(false);

  // 평량 기반 두께 자동 계산 (mm)
  // 일반적인 종이 두께 공식: 두께 ≈ 평량 × 비용적 / 1000
  // 비코팅지: 약 1.2~1.4, 코팅지: 약 0.85~0.95
  const calculateThickness = (grammage: number, isCoated: boolean): number => {
    if (grammage <= 0) return 0;
    const bulkFactor = isCoated ? 0.9 : 1.3;
    return Math.round(grammage * bulkFactor) / 1000;
  };

  const customThickness = calculateThickness(customGrammage, customIsCoated);

  // Inline edit
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  // Excel download selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Material 삭제 시 사용 중인 상품 목록 모달
  const [showProductsInUseModal, setShowProductsInUseModal] = useState(false);
  const [productsInUse, setProductsInUse] = useState<ProductInUse[]>([]);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState("");

  // 잉크 탭용 데이터 로드
  const loadInkTabData = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    setLoading(true);
    try {
      const [inkConfigs, inks] = await Promise.all([
        companyProductSystemUseCase.getMaterials(cid, "INK_CONFIG"),
        companyProductSystemUseCase.getMaterials(cid, "INK"),
      ]);
      setInkConfigMaterials(inkConfigs);
      setInkMaterials(inks);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [cid]);

  const loadMaterials = useCallback(async () => {
    if (!Number.isFinite(cid)) return;
    // 잉크 탭은 별도 로드
    if (typeFilter === "INK_TAB") {
      await loadInkTabData();
      return;
    }
    setLoading(true);
    try {
      const typeParam = typeFilter;
      const result = await companyProductSystemUseCase.getMaterials(cid, typeParam);
      setMaterials(result);
    } catch (e) {
      console.error(e);
      alert("자재 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [cid, typeFilter, loadInkTabData]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  // Load paper specs once
  useEffect(() => {
    if (!Number.isFinite(cid)) return;
    companyProductSystemUseCase.getPaperSpecs(cid, true).then(setPaperSpecs).catch(() => {});
  }, [cid]);

  // 낱장 용지만 PaperSpec(종이 종류 + 평량) 기반 2단계 선택이다.
  // 롤 용지(현수막 원단 등)는 평량으로 구분하지 않으므로 이름만 입력한다.
  const isPaperType = newType === "SHEET_PAPER";

  // 용지 기본 이름 추출 (평량 제거): "아트지 180g" → "아트지", "스노우지(200g)" → "스노우지"
  const extractBaseName = (name: string): string => {
    return name.replace(/\s*\(?\d+g\)?$/i, "").trim();
  };

  // 용지 종류별 그룹핑 (기본 이름 → PaperSpec[])
  const paperSpecGroups = React.useMemo(() => {
    const groups: Record<string, PaperSpecDto[]> = {};
    paperSpecs.forEach((ps) => {
      const baseName = extractBaseName(ps.name);
      if (!groups[baseName]) groups[baseName] = [];
      groups[baseName].push(ps);
    });
    // 각 그룹 내에서 평량 순으로 정렬
    Object.values(groups).forEach((specs) => specs.sort((a, b) => a.grammage - b.grammage));
    return groups;
  }, [paperSpecs]);

  // 선택된 용지 종류의 평량 목록
  const availableGrammages = selectedPaperBaseName ? paperSpecGroups[selectedPaperBaseName] || [] : [];

  // 낱장 용지(SHEET_PAPER)를 종이 종류로 묶어 평량 칩으로 보여준다. 롤 용지는 원단별 카드로 보여준다
  interface GroupedMaterial {
    baseName: string;
    type: string;
    materials: (MaterialDto & { spec: PaperSpecDto })[];
  }

  const groupedPaperMaterials = React.useMemo((): GroupedMaterial[] => {
    const paperMaterials = materials.filter(m =>
      m.type === "SHEET_PAPER" && m.paperSpecId
    );

    const groups: Record<string, GroupedMaterial> = {};

    paperMaterials.forEach(m => {
      const spec = paperSpecs.find(ps => ps.id === m.paperSpecId);
      if (!spec) return;

      const baseName = extractBaseName(spec.name);
      const groupKey = `${baseName}_${m.type}`;
      if (!groups[groupKey]) {
        groups[groupKey] = { baseName, type: m.type, materials: [] };
      }
      groups[groupKey].materials.push({ ...m, spec });
    });

    // 평량 순으로 정렬
    Object.values(groups).forEach(g => {
      g.materials.sort((a, b) => a.spec.grammage - b.spec.grammage);
    });

    return Object.values(groups);
  }, [materials, paperSpecs]);

  // 그룹핑되지 않은 자재 (ROLL_PAPER, INK, INK_CONFIG, OTHER 또는 paperSpecId가 없는 경우)
  const ungroupedMaterials = materials.filter(m =>
    m.type !== "SHEET_PAPER" || !m.paperSpecId
  );

  // 필터링된 그룹핑 자재
  const filteredGroupedMaterials = groupedPaperMaterials.filter(g =>
    g.type === typeFilter
  );

  // 필터링된 비그룹 자재
  const filteredUngroupedMaterials = ungroupedMaterials.filter(m =>
    m.type === typeFilter
  );

  // 용지 타입일 때 PaperSpec 선택 시 자동 이름 생성: 평량 없이 용지 종류명만 사용
  const selectedSpec = isPaperType && newPaperSpecId ? paperSpecs.find(ps => ps.id === newPaperSpecId) : null;
  const autoName = selectedSpec ? extractBaseName(selectedSpec.name) : "";

  const handleCreate = async () => {
    // 커스텀 용지 생성
    if (isPaperType && isCustomPaper) {
      if (!customPaperName.trim()) { alert("용지 이름을 입력해주세요."); return; }
      if (customGrammage <= 0) { alert("평량을 입력해주세요."); return; }

      const customFinalName = customPaperName.trim();
      try {
        // 낱장 용지: Material만 생성 (grammage 포함 - PaperSpec 자동 생성)
        await companyProductSystemUseCase.createMaterial(cid, {
          name: customFinalName,
          type: "SHEET_PAPER",
          grammage: customGrammage,  // PaperSpec 자동 생성
        });
        await loadMaterials();
        setShowAddModal(false);
        // 상태 초기화
        setNewName("");
        setNewPaperSpecId(undefined);
        setAddStep(1);
        setSelectedPaperBaseName("");
        setIsCustomPaper(false);
        setCustomPaperName("");
        setCustomGrammage(0);
        setCustomIsCoated(false);
      } catch (e) {
        console.error(e);
        alert("자재 추가에 실패했습니다.");
      }
      return;
    }

    const finalName = isPaperType ? autoName : newName.trim();
    if (!finalName) { alert(isPaperType ? "용지 규격을 선택해주세요." : "이름을 입력해주세요."); return; }
    if (isPaperType && !newPaperSpecId) { alert("용지 규격을 선택해주세요."); return; }
    try {
      if (newType === "ROLL_PAPER") {
        // ROLL_PAPER: 원단 이름만으로 Material 생성 (평량·PaperSpec 없음).
        // 규격(롤 폭)은 생성 후 롤 용지 화면의 "규격 추가"로 자재별로 붙인다.
        await companyProductSystemUseCase.createMaterial(cid, {
          name: finalName,
          type: "ROLL_PAPER",
        });
      } else if (newType === "INK_CONFIG") {
        // INK_CONFIG: Material + InkConfig 동시 생성 (1:1 관계)
        const material = await companyProductSystemUseCase.createMaterial(cid, {
          name: finalName,
          type: "INK_CONFIG",
        });
        // InkConfig 생성 (기본 가격 구간: 1매부터 0원)
        await companyProductSystemApiService.createInkConfig(cid, material.id, {
          name: finalName,
          inkIds: [],
          priceTiers: [{ minQuantity: 1, price: 0 }],
        } as any);
      } else if (newType === "INK") {
        // INK: 기존 INK Material에 잉크 추가 (없으면 생성)
        let inkMaterial = inkMaterials[0];
        if (!inkMaterial) {
          // INK 타입 자재가 없으면 생성
          inkMaterial = await companyProductSystemUseCase.createMaterial(cid, {
            name: "잉크",
            type: "INK",
          });
        }
        // 잉크 추가
        await companyProductSystemApiService.createInk(cid, inkMaterial.id, {
          name: finalName,
          inkColor: newInkColor || undefined,
          isDeletable: true,
        } as any);
      } else if (newType === "OTHER") {
        // OTHER: Material + OtherMaterial 동시 생성 (1:1 관계)
        const material = await companyProductSystemUseCase.createMaterial(cid, {
          name: finalName,
          type: "OTHER",
        });
        // OtherMaterial 생성 (기본 가격 0원)
        await companyProductSystemApiService.createOtherMaterial(cid, material.id, {
          name: finalName,
          price: 0,
        } as any);
      } else {
        // 기타 타입: 기존 로직 (SHEET_PAPER 등)
        const requestPayload = {
          name: finalName,
          type: newType,
          paperSpecId: isPaperType ? newPaperSpecId : undefined,
        };
        console.log("[MaterialCreate] 요청 데이터:", requestPayload);
        await companyProductSystemUseCase.createMaterial(cid, requestPayload);
      }
      await loadMaterials();
      setShowAddModal(false);
      setNewName("");
      setNewPaperSpecId(undefined);
      setNewInkCmyk({ c: 0, m: 0, y: 0, k: 0 });
      setAddStep(1);
      setSelectedPaperBaseName("");
      // 커스텀 용지 상태 초기화
      setIsCustomPaper(false);
      setCustomPaperName("");
      setCustomGrammage(0);
      setCustomIsCoated(false);
    } catch (e) {
      console.error(e);
      alert("자재 추가에 실패했습니다.");
    }
  };

  const handleUpdate = async (materialId: number) => {
    if (!editingName.trim()) return;
    try {
      await companyProductSystemUseCase.updateMaterial(cid, materialId, { name: editingName.trim() });
      await loadMaterials();
      setEditingId(null);
    } catch (e) {
      console.error(e);
      alert("수정에 실패했습니다.");
    }
  };

  /**
   * 자재를 한 건씩 순서대로 삭제하고, 실패한 건은 모아서 한 번에 안내한다.
   * 확인창은 부르는 쪽에서 띄운다 (카드 전체 삭제는 확인 한 번으로 여러 평량을 지운다).
   */
  const deleteMaterials = async (targets: { id: number; label: string }[]) => {
    const failures: { label: string; message: string; products: ProductInUse[] }[] = [];
    const deletedIds = new Set<number>();

    for (const target of targets) {
      try {
        await companyProductSystemUseCase.deleteMaterial(cid, target.id);
        deletedIds.add(target.id);
      } catch (e: any) {
        console.error("Delete error:", e);
        failures.push({
          label: target.label,
          message: e?.message || "삭제에 실패했습니다.",
          products: e?.products || [],
        });
      }
    }

    await loadMaterials();
    if (expandedId !== null && deletedIds.has(expandedId)) setExpandedId(null);
    if (failures.length === 0) return;

    const inUse = failures.filter((f) => f.products.length > 0);
    const others = failures.filter((f) => f.products.length === 0);
    const lines: string[] = [];
    if (targets.length > 1) {
      lines.push(`${targets.length}개 중 ${deletedIds.size}개를 삭제했습니다.`);
    }
    if (inUse.length > 0) {
      lines.push(
        targets.length > 1
          ? `상품에서 사용 중이라 삭제하지 못했습니다: ${inUse.map((f) => f.label).join(", ")}`
          : "이 자재를 사용하는 상품이 있어 삭제할 수 없습니다."
      );
    }
    others.forEach((f) => lines.push(`${f.label}: ${f.message}`));

    if (inUse.length === 0) {
      alert(lines.join("\n"));
      return;
    }

    // 여러 자재가 같은 상품에 걸려 있을 수 있어 상품은 한 번씩만 보여준다
    const products = Array.from(
      new Map(inUse.flatMap((f) => f.products).map((p) => [p.id, p])).values()
    );
    setProductsInUse(products);
    setDeleteErrorMsg(lines.join("\n"));
    setShowProductsInUseModal(true);
  };

  const handleDelete = async (materialId: number, label: string) => {
    if (!window.confirm("이 자재를 삭제하시겠습니까?")) return;
    await deleteMaterials([{ id: materialId, label }]);
  };

  // 용지 카드 안의 평량 하나만 삭제 (materials 1행 = 용지 1종 + 평량 1개)
  const handleDeleteGrammage = async (baseName: string, material: MaterialDto & { spec: PaperSpecDto }) => {
    const label = `${baseName} ${material.spec.grammage}g`;
    if (!window.confirm(`${label}을(를) 삭제하시겠습니까?`)) return;
    await deleteMaterials([{ id: material.id, label }]);
  };

  // 용지 카드 전체(모든 평량) 삭제 - 확인은 한 번만
  const handleDeleteGroup = async (group: GroupedMaterial) => {
    const message = group.materials.length >= 2
      ? `${group.baseName}의 모든 평량(${group.materials.length}개)을 삭제하시겠습니까?`
      : "이 자재를 삭제하시겠습니까?";
    if (!window.confirm(message)) return;
    await deleteMaterials(
      group.materials.map((m) => ({ id: m.id, label: `${group.baseName} ${m.spec.grammage}g` }))
    );
  };

  const toggleExpand = (id: number) => {
    guardedSetExpanded(expandedId === id ? null : id);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadExcel = async () => {
    const selected = materials.filter((m) => selectedIds.has(m.id));
    if (selected.length === 0) { alert("선택된 자재가 없습니다."); return; }
    try {
      const templatePath = "/assets/files/자재발주서납품조건.xlsx";
      const response = await fetch(templatePath);
      if (!response.ok) throw new Error("템플릿 파일을 불러올 수 없습니다.");

      const templateBuffer = await response.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(templateBuffer);
      const worksheet = workbook.getWorksheet(1);
      if (!worksheet) throw new Error("워크시트를 찾을 수 없습니다.");

      const maxItems = Math.min(selected.length, 16);
      for (let i = 0; i < maxItems; i++) {
        worksheet.getCell(`B${15 + i}`).value = selected[i].name || "";
      }

      const today = new Date();
      const dateString = today.toISOString().slice(0, 10).replace(/-/g, "");
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `자재발주서_${dateString}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel 다운로드 중 오류:", error);
      alert("Excel 파일 생성 중 오류가 발생했습니다.");
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-[22px] font-bold text-[#222]">자재 관리</h1>
          <div className="flex gap-2">
            <button onClick={downloadExcel}
              className="px-4 py-2 text-[13px] font-medium text-[#555] bg-white border border-[#ddd] rounded-[8px] hover:bg-[#f5f5f5]">
              자재발주서 다운로드
            </button>
            <button onClick={() => {
              setNewName("");
              setNewPaperSpecId(undefined);
              setSelectedPaperBaseName("");
              setNewInkCmyk({ c: 0, m: 0, y: 0, k: 0 });
              // 잉크 탭에서는 INK_CONFIG를 기본으로 (인쇄 구성 추가가 더 일반적)
              setNewType(typeFilter === "INK_TAB" ? "INK_CONFIG" : typeFilter);
              setAddStep(1);
              setShowAddModal(true);
            }}
              className="flex items-center gap-1 px-4 py-2 text-[13px] font-medium text-white bg-[#222] rounded-[8px] hover:bg-[#333]">
              <Plus className="w-[16px] h-[16px]" /> 자재 추가
            </button>
          </div>
        </div>

        {/* Type Filter Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#eee]">
          {MATERIAL_TYPES.map((mt) => (
            <button key={mt.key}
              onClick={() => { if (confirmLeaveExpanded()) { setTypeFilter(mt.key); setExpandedId(null); } }}
              className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                typeFilter === mt.key
                  ? "border-[#222] text-[#222]"
                  : "border-transparent text-[#999] hover:text-[#555]"
              }`}>
              {mt.label}
            </button>
          ))}
        </div>

        {/* Material List */}
        {loading ? (
          <p className="text-[14px] text-[#999] py-8 text-center">불러오는 중...</p>
        ) : typeFilter === "INK_TAB" ? (
          /* 잉크 탭 전용 UI */
          <InkTabContent
            companyId={cid}
            inkConfigMaterials={inkConfigMaterials}
            inkMaterials={inkMaterials}
            onDeleteMaterial={(id) =>
              deleteMaterials([{ id, label: inkConfigMaterials.find((m) => m.id === id)?.name ?? "인쇄 구성" }])
            }
            onRefresh={loadMaterials}
          />
        ) : materials.length === 0 ? (
          <p className="text-[14px] text-[#bbb] py-8 text-center">등록된 자재가 없습니다.</p>
        ) : (
          <div className="space-y-6">
            {/* 그룹핑된 용지 자재 (카드 + 별도 확장 영역) */}
            {filteredGroupedMaterials.length > 0 && (
              <div className="space-y-4">
                {/* 카드 그리드 */}
                <div className="flex flex-wrap gap-4">
                  {filteredGroupedMaterials.map((group) => {
                    const badgeColor = TYPE_BADGE_COLORS[group.type] || TYPE_BADGE_COLORS.OTHER;
                    const expandedMaterial = group.materials.find(m => expandedId === m.id);

                    return (
                      <div
                        key={`${group.baseName}_${group.type}`}
                        className={`w-[200px] bg-white border rounded-[12px] p-4 relative group/card hover:shadow-md transition-all ${
                          expandedMaterial ? "border-[#4285F4] ring-2 ring-[#4285F4]/20" : "border-[#e0e0e0]"
                        }`}
                      >
                        {/* 삭제 버튼 */}
                        <button
                          onClick={() => handleDeleteGroup(group)}
                          className="absolute top-3 right-3 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover/card:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                        >
                          <Trash2 className="w-[14px] h-[14px]" />
                        </button>

                        {/* 타입 뱃지 */}
                        <span
                          className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-[4px] mb-2"
                          style={{ backgroundColor: badgeColor.bg, color: badgeColor.text }}
                        >
                          {MATERIAL_TYPE_LABELS[group.type] || group.type}
                        </span>

                        {/* 용지 이름 */}
                        <p className="text-[15px] font-medium text-[#222] mb-3">{group.baseName}</p>

                        {/* 평량 chip 리스트 */}
                        <div className="flex flex-wrap gap-1.5">
                          {group.materials.map((m) => (
                            <button
                              key={m.id}
                              onClick={() => guardedSetExpanded(expandedId === m.id ? null : m.id)}
                              className={`px-2 py-1 text-[11px] font-medium rounded-[4px] transition-colors ${
                                expandedId === m.id
                                  ? "bg-[#4285F4] text-white"
                                  : "bg-[#f0f0f0] text-[#666] hover:bg-[#e0e0e0]"
                              }`}
                            >
                              {m.spec.grammage}g
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* 확장된 하위 리소스 - 카드 리스트 아래 전체 너비로 표시 */}
                {(() => {
                  // 현재 확장된 자재 찾기
                  const expandedGroup = filteredGroupedMaterials.find(g =>
                    g.materials.some(m => expandedId === m.id)
                  );
                  const expandedMaterial = expandedGroup?.materials.find(m => expandedId === m.id);

                  if (!expandedGroup || !expandedMaterial) return null;

                  return (
                    <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-[15px] font-medium text-[#222]">
                          {expandedGroup.baseName}
                        </span>
                        <span className="px-2 py-0.5 text-[12px] font-medium bg-[#4285F4] text-white rounded-[4px]">
                          {expandedMaterial.spec.grammage}g
                        </span>
                        <span className="text-[13px] text-[#888]">{expandedMaterial.name}</span>
                        <button
                          onClick={() => handleDeleteGrammage(expandedGroup.baseName, expandedMaterial)}
                          className="ml-auto flex items-center gap-1 px-2 py-1 text-[12px] text-[#999] hover:text-[#F24A4A]"
                        >
                          <Trash2 className="w-[14px] h-[14px]" />
                          {expandedMaterial.spec.grammage}g 삭제
                        </button>
                        <button
                          onClick={() => guardedSetExpanded(null)}
                          className="text-[#999] hover:text-[#666]"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <MaterialSubResources companyId={cid} material={expandedMaterial} onRollUnpricedChange={handleRollUnpricedChange} />
                    </div>
                  );
                })()}
              </div>
            )}

            {/* 비그룹 자재 (기존 리스트 형태) */}
            {filteredUngroupedMaterials.length > 0 && (
              <div className="space-y-3">
                {filteredUngroupedMaterials.map((material) => {
                  const isExpanded = expandedId === material.id;
                  const isEditing = editingId === material.id;
                  const badgeColor = TYPE_BADGE_COLORS[material.type] || TYPE_BADGE_COLORS.OTHER;

                  return (
                    <div key={material.id} className="bg-white border border-[#eee] rounded-[12px] overflow-hidden">
                      {/* Material Header */}
                      <div className="flex items-center gap-3 px-5 py-4">
                        <div onClick={() => toggleSelect(material.id)} className="cursor-pointer">
                          <CheckBox isEnabled={selectedIds.has(material.id)} />
                        </div>

                        <button onClick={() => toggleExpand(material.id)} className="text-[#999] hover:text-[#555]">
                          {isExpanded
                            ? <ChevronDown className="w-[18px] h-[18px]" />
                            : <ChevronRight className="w-[18px] h-[18px]" />}
                        </button>

                        {isEditing ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input type="text" value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="flex-1 border border-[#e0e0e0] rounded-[8px] px-3 py-1.5 text-[14px] focus:border-[#4285F4] focus:outline-none"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleUpdate(material.id);
                                if (e.key === "Escape") setEditingId(null);
                              }}
                            />
                            <button onClick={() => handleUpdate(material.id)}
                              className="px-3 py-1 text-[12px] text-white bg-[#222] rounded-[6px]">저장</button>
                            <button onClick={() => setEditingId(null)}
                              className="px-3 py-1 text-[12px] text-[#666]">취소</button>
                          </div>
                        ) : (
                          <button onClick={() => toggleExpand(material.id)} className="flex-1 text-left">
                            <span className="text-[15px] font-medium text-[#222]">{material.name}</span>
                          </button>
                        )}

                        <span className="px-2.5 py-1 text-[11px] font-medium rounded-[4px]"
                          style={{ backgroundColor: badgeColor.bg, color: badgeColor.text }}>
                          {MATERIAL_TYPE_LABELS[material.type] || material.type}
                        </span>

                        {/* 롤 원단은 평량으로 구분하지 않으므로 종이 규격을 표시하지 않는다 */}
                        {material.type !== "ROLL_PAPER" && material.paperSpecId && (() => {
                          const spec = paperSpecs.find((ps) => ps.id === material.paperSpecId);
                          return spec ? (
                            <span className="text-[11px] px-2 py-0.5 bg-[#f5f5f5] text-[#888] rounded-[4px]">
                              {spec.name} ({spec.grammage}g{spec.isCoated ? ", 코팅" : ""})
                            </span>
                          ) : null;
                        })()}

                        <span className="text-[12px] text-[#bbb] min-w-[80px]">
                          {material.createdAt?.toString().slice(0, 10) ?? ""}
                        </span>

                        {!isEditing && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setEditingId(material.id); setEditingName(material.name); }}
                              className="text-[12px] text-[#4285F4] hover:text-[#3367D6]">수정</button>
                            <button onClick={() => handleDelete(material.id, material.name)}
                              className="text-[#ccc] hover:text-[#F24A4A]">
                              <Trash2 className="w-[15px] h-[15px]" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded Sub-resources */}
                      {isExpanded && (
                        <div className="px-5 pb-5 pt-1 border-t border-[#f0f0f0]">
                          <MaterialSubResources companyId={cid} material={material} onRollUnpricedChange={handleRollUnpricedChange} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* 빈 상태 */}
            {filteredGroupedMaterials.length === 0 && filteredUngroupedMaterials.length === 0 && (
              <p className="text-[14px] text-[#bbb] py-8 text-center">해당 타입의 자재가 없습니다.</p>
            )}
          </div>
        )}

        {/* Add Material Modal - 2 Steps for Paper Types */}
        {showAddModal && (
          <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
            <div className="w-[420px] rounded-[12px] bg-white p-6 shadow-lg">
              {/* 헤더 */}
              <div className="mb-5">
                <h3 className="text-[16px] font-bold text-[#222]">
                  {typeFilter === "INK_TAB"
                    ? (newType === "INK" ? "잉크 추가" : "인쇄 구성 추가")
                    : `${MATERIAL_TYPE_LABELS[newType] || newType} 추가`}
                  {isPaperType && addStep === 2 ? " - 평량 선택" : ""}
                </h3>
              </div>

              {/* 잉크 탭용 타입 선택 */}
              {typeFilter === "INK_TAB" && (
                <div className="mb-4">
                  <label className="block text-[12px] font-medium text-[#555] mb-2">추가할 항목</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewType("INK_CONFIG")}
                      className={`flex-1 py-2.5 text-[13px] font-medium rounded-[8px] border transition-colors ${
                        newType === "INK_CONFIG"
                          ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                          : "border-[#e0e0e0] text-[#666] hover:border-[#4285F4]"
                      }`}
                    >
                      인쇄 구성
                    </button>
                    <button
                      onClick={() => setNewType("INK")}
                      className={`flex-1 py-2.5 text-[13px] font-medium rounded-[8px] border transition-colors ${
                        newType === "INK"
                          ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                          : "border-[#e0e0e0] text-[#666] hover:border-[#4285F4]"
                      }`}
                    >
                      잉크
                    </button>
                  </div>
                </div>
              )}

              {/* 자재별 입력 UI */}
              {isPaperType ? (
                <div className="mb-5">
                  {isCustomPaper ? (
                    /* 커스텀 용지 입력 폼 */
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={() => setIsCustomPaper(false)}
                          className="text-[#4285F4] hover:text-[#3367D6] text-[13px]"
                        >
                          ← 용지 규격 선택으로 돌아가기
                        </button>
                      </div>

                      <div>
                        <label className="block text-[12px] font-medium text-[#555] mb-1">용지 이름</label>
                        <input
                          type="text"
                          value={customPaperName}
                          onChange={(e) => setCustomPaperName(e.target.value)}
                          className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none"
                          placeholder="예: 아트지, 스노우지"
                          autoFocus
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[12px] font-medium text-[#555] mb-1">평량 (g/m²)</label>
                          <input
                            type="number"
                            value={customGrammage || ""}
                            onChange={(e) => setCustomGrammage(Number(e.target.value))}
                            className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none"
                            placeholder="예: 150"
                            min={1}
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-[#555] mb-1">두께 (mm) - 자동계산</label>
                          <div className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] bg-[#f9f9f9] text-[#666]">
                            {customThickness > 0 ? `${customThickness.toFixed(3)} mm` : "-"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="customIsCoated"
                          checked={customIsCoated}
                          onChange={(e) => setCustomIsCoated(e.target.checked)}
                          className="w-4 h-4 rounded border-[#e0e0e0] text-[#4285F4] focus:ring-[#4285F4]"
                        />
                        <label htmlFor="customIsCoated" className="text-[13px] text-[#555]">
                          코팅지
                        </label>
                        <span className="text-[11px] text-[#999] ml-2">
                          (코팅지는 두께가 얇게 계산됩니다)
                        </span>
                      </div>

                      {/* 롤 용지 규격은 자재 생성 후 롤 용지 화면의 "규격 추가"로 자재별로 관리한다. */}

                      {customPaperName && customGrammage > 0 && (
                        <div className="p-3 bg-[#f5f8ff] rounded-[8px] border border-[#e0e0e0]">
                          <p className="text-[13px] text-[#333]">
                            자재 이름: <span className="font-medium">{customPaperName}({customGrammage}g)</span>
                          </p>
                          <p className="text-[12px] text-[#666] mt-1">
                            {customIsCoated ? "코팅지" : "비코팅지"} · 두께 약 {customThickness.toFixed(3)}mm
                          </p>
                        </div>
                      )}
                    </div>
                  ) : paperSpecs.length === 0 && !isCustomPaper ? (
                    <div className="space-y-3">
                      <p className="text-[12px] text-[#999]">등록된 용지 규격이 없습니다.</p>
                      <button
                        onClick={() => setIsCustomPaper(true)}
                        className="w-full py-3 border-2 border-dashed border-[#4285F4] rounded-[10px] text-[#4285F4] text-[14px] font-medium hover:bg-[#F5F8FF] transition-colors"
                      >
                        + 커스텀 용지 직접 추가
                      </button>
                    </div>
                  ) : addStep === 1 ? (
                    /* Step 1: 용지 종류 선택 */
                    <>
                      <label className="block text-[12px] font-medium text-[#555] mb-2">용지 종류 선택</label>
                      <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto mb-3">
                        {Object.entries(paperSpecGroups).map(([baseName, specs]) => (
                          <button
                            key={baseName}
                            onClick={() => {
                              setSelectedPaperBaseName(baseName);
                              setAddStep(2);
                              // 평량이 하나만 있으면 자동 선택
                              if (specs.length === 1) {
                                setNewPaperSpecId(specs[0].id);
                              }
                            }}
                            className="flex flex-col items-start p-3 border border-[#e0e0e0] rounded-[10px] hover:border-[#4285F4] hover:bg-[#F5F8FF] transition-colors text-left"
                          >
                            <span className="text-[14px] font-medium text-[#333] mb-1">{baseName}</span>
                            <span className="text-[11px] text-[#888]">
                              {specs.map(s => `${s.grammage}g`).join(", ")}
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* 커스텀 용지 추가 버튼 */}
                      <button
                        onClick={() => setIsCustomPaper(true)}
                        className="w-full py-2.5 border-2 border-dashed border-[#ccc] rounded-[10px] text-[#888] text-[13px] font-medium hover:border-[#4285F4] hover:text-[#4285F4] hover:bg-[#F5F8FF] transition-colors"
                      >
                        + 커스텀 용지 직접 추가
                      </button>
                    </>
                  ) : (
                    /* Step 2: 평량 선택 */
                    <>
                      <div className="mb-3">
                        <span className="text-[14px] font-medium text-[#333]">{selectedPaperBaseName}</span>
                      </div>
                      <label className="block text-[12px] font-medium text-[#555] mb-2">평량 선택</label>
                      <div className="grid grid-cols-3 gap-2">
                        {availableGrammages.map((ps) => (
                          <button
                            key={ps.id}
                            onClick={() => setNewPaperSpecId(ps.id)}
                            className={`p-3 border rounded-[10px] transition-colors text-center ${
                              newPaperSpecId === ps.id
                                ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                                : "border-[#e0e0e0] hover:border-[#4285F4] hover:bg-[#F5F8FF] text-[#333]"
                            }`}
                          >
                            <span className="text-[15px] font-bold">{ps.grammage}g</span>
                            <p className="text-[10px] text-[#888] mt-0.5">{ps.thicknessMm}mm</p>
                          </button>
                        ))}
                      </div>
                      {autoName && (
                        <p className="mt-3 text-[13px] text-[#333]">
                          자재 이름: <span className="font-medium">{autoName}</span>
                        </p>
                      )}

                      {/* 롤 용지 규격은 자재 생성 후 롤 용지 화면의 "규격 추가"로 자재별로 관리한다. */}
                    </>
                  )}
                </div>
              ) : newType === "INK" ? (
                /* 잉크 추가: 이름 + 색상 */
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-[12px] font-medium text-[#555] mb-1">잉크 이름</label>
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none"
                      placeholder="예: 빨강"
                      autoFocus
                    />
                  </div>
                  {/* 색상 선택 */}
                  <div>
                    <label className="block text-[12px] font-medium text-[#555] mb-2">잉크 색상</label>
                    {/* 색상 미리보기 + CMYK 입력 (2x2 그리드) */}
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowNewColorPicker(!showNewColorPicker)}
                          className="w-[72px] h-[72px] rounded-[8px] border-2 border-[#e0e0e0] cursor-pointer hover:border-[#bbb] transition-colors"
                          style={{ backgroundColor: newInkColor }}
                        />
                        {/* Color Picker 드롭다운 */}
                        {showNewColorPicker && (
                          <>
                            <div className="fixed inset-0 z-[55]" onClick={() => setShowNewColorPicker(false)} />
                            <div className="absolute top-[80px] left-0 z-[56] bg-white rounded-[12px] shadow-xl border border-[#e0e0e0] p-3">
                              <div className="flex justify-end mb-2">
                                <button onClick={() => setShowNewColorPicker(false)} className="text-[#888] hover:text-[#222]">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                              <style>{`
                                .new-color-picker .react-colorful {
                                  width: 200px !important;
                                  height: auto !important;
                                }
                                .new-color-picker .react-colorful__saturation {
                                  border-radius: 6px !important;
                                  height: 150px !important;
                                }
                                .new-color-picker .react-colorful__hue {
                                  height: 12px !important;
                                  border-radius: 6px !important;
                                  margin-top: 10px !important;
                                }
                                .new-color-picker .react-colorful__pointer {
                                  width: 16px !important;
                                  height: 16px !important;
                                  border-width: 2px !important;
                                }
                              `}</style>
                              <div className="new-color-picker">
                                <HexColorPicker color={newInkColor} onChange={(color) => setNewInkCmyk(hexToCmyk(color))} />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {['c', 'm', 'y', 'k'].map((key) => (
                          <div key={key} className="flex items-center gap-1.5">
                            <span className="text-[12px] font-semibold text-[#555] uppercase w-4">{key}</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={newInkCmyk[key as keyof typeof newInkCmyk]}
                              onChange={(e) => setNewInkCmyk(prev => ({ ...prev, [key]: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                              className="w-16 border border-[#e0e0e0] rounded-[6px] px-2 py-1.5 text-[13px] text-center focus:border-[#4285F4] focus:outline-none"
                            />
                            <span className="text-[11px] text-[#888]">%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : !isPaperType ? (
                /* 용지 외 타입: 이름 직접 입력 */
                <div className="mb-4">
                  <label className="block text-[12px] font-medium text-[#555] mb-1">
                    {newType === "INK_CONFIG" ? "인쇄 구성 이름" : newType === "ROLL_PAPER" ? "원단 이름" : "자재 이름"}
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] focus:border-[#4285F4] focus:outline-none"
                    placeholder={
                      newType === "INK_CONFIG" ? "예: 4도 인쇄" :
                      newType === "OTHER" ? "예: 코팅액, 접착제" :
                      newType === "ROLL_PAPER" ? "예: 현수막 원단, 메쉬 원단" :
                      "예: 자재명"
                    }
                    autoFocus
                  />
                </div>
              ) : null}

              {/* 버튼 */}
              <div className="flex justify-between gap-2">
                <div>
                  {/* 이전 버튼 - 용지 타입에서 평량 선택 단계일 때만 표시 (커스텀 모드 제외) */}
                  {isPaperType && addStep === 2 && !isCustomPaper && (
                    <button
                      onClick={() => {
                        setAddStep(1);
                        setNewPaperSpecId(undefined);
                        setSelectedPaperBaseName("");
                      }}
                      className="px-4 py-2 text-[13px] text-[#666] rounded-[8px] hover:bg-[#f5f5f5]"
                    >
                      ← 이전
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewName("");
                      setNewPaperSpecId(undefined);
                      setNewInkCmyk({ c: 0, m: 0, y: 0, k: 0 });
                      setAddStep(1);
                      setSelectedPaperBaseName("");
                      // 커스텀 용지 상태 초기화
                      setIsCustomPaper(false);
                      setCustomPaperName("");
                      setCustomGrammage(0);
                      setCustomIsCoated(false);
                    }}
                    className="px-4 py-2 text-[13px] text-[#666] rounded-[8px] hover:bg-[#f5f5f5]"
                  >
                    취소
                  </button>
                  {/* 추가 버튼: 커스텀 모드이거나, 용지 타입 Step 2이거나, 용지 외 타입 */}
                  {(isCustomPaper || !isPaperType || addStep === 2) && (
                    <button
                      onClick={handleCreate}
                      disabled={
                        isCustomPaper
                          ? (!customPaperName.trim() || customGrammage <= 0)
                          : (isPaperType ? !newPaperSpecId : !newName.trim())
                      }
                      className="px-5 py-2 text-[13px] font-medium text-white bg-[#222] rounded-[8px] hover:bg-[#333] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
                    >
                      추가
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 사용 중인 상품 목록 모달 */}
        {showProductsInUseModal && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[1000]"
              onClick={() => setShowProductsInUseModal(false)}
            />

            {/* Dialog */}
            <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
              <div
                className="bg-white rounded-[20px] shadow-2xl max-w-[480px] w-full relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  {/* Close button */}
                  <button
                    onClick={() => setShowProductsInUseModal(false)}
                    className="absolute top-6 right-6 text-black hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Title */}
                  <h3 className="text-[20px] font-bold text-center text-[#222] mb-6">
                    삭제할 수 없습니다
                  </h3>

                  {/* Info Box */}
                  <div className="bg-[#EBF3FF] rounded-[12px] p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4285F4] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-[12px] font-bold">!</span>
                      </div>
                      <p className="text-[14px] text-[#4285F4] leading-relaxed whitespace-pre-line">
                        {deleteErrorMsg}
                        {"\n"}먼저 해당 상품의 용지·인쇄 구성·옵션에서 이 자재를 빼 주세요.
                      </p>
                    </div>
                  </div>

                  {/* Product Cards - 좌우 스크롤 */}
                  {productsInUse.length > 0 && (
                    <div className="mb-6 -mx-2">
                      <div className="flex gap-3 overflow-x-auto px-2 pb-2">
                        {productsInUse.map((product) => (
                          <a
                            key={product.id}
                            href={`/company/${cid}/products/${product.id}`}
                            className="flex-shrink-0 w-[120px] border border-[#eee] rounded-[10px] overflow-hidden hover:border-[#4285F4] hover:shadow-md transition-all cursor-pointer bg-white"
                          >
                            {/* 상품 이미지 */}
                            <div className="w-[120px] h-[80px] bg-[#f5f5f5] flex items-center justify-center overflow-hidden">
                              {product.thumbnail ? (
                                <img
                                  src={product.thumbnail}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="text-[#ccc] text-[11px]">이미지 없음</div>
                              )}
                            </div>
                            {/* 상품 이름 */}
                            <div className="px-2 py-2">
                              <p className="text-[12px] font-medium text-[#333] truncate">{product.name}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowProductsInUseModal(false)}
                      className="flex-1 py-[14px] border border-[#ddd] bg-white text-[#333] rounded-[8px] font-medium text-[15px] hover:bg-[#f5f5f5] transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={() => setShowProductsInUseModal(false)}
                      className="flex-1 py-[14px] bg-[#4285F4] hover:bg-[#3367D6] text-white rounded-[8px] font-medium text-[15px] transition-colors"
                    >
                      확인
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default MaterialManagementPage;
